import React, {
  type ComponentType,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MODAL_TYPES } from '@components/modals';
import {
  countSetDefaults,
  mapAppointmentTypeToDefaults,
  TypeDefaultsPreview,
} from '@components/ui/cards/TypeDefaultsPreview';
import {
  LabelChipRow,
  LabelPicker,
  useAppointmentLabelsEnabled,
  useGetLabelsQuery,
} from '@features/appointment-labels';
import type { ExpandableFieldItem } from '@features/appointment-types';
import { useTemplateNameById } from '@features/appointment-types/hooks/useTemplateNameById';
import { useAppointmentTypes } from '@features/appointment-types/store/hooks';
import type { AppointmentType } from '@features/appointment-types/types';
import { getRecurrenceText } from '@features/appointments/utils/dateTimeFormatters';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { useForms } from '@features/forms';
import { useClinicians } from '@features/organizations';
import {
  useAccessToken,
  useCanCreatePatients,
  useOrganizationId,
  useSubjectLanguage,
} from '@features/user';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useDebounce } from '@hooks/useDebounce';
import { Avatar, Chip as JotChip, ProviderBadge } from '@jotpsych/ui';
import AddIcon from '@mui/icons-material/Add';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import LabelOutlined from '@mui/icons-material/LabelOutlined';
import LocalHospitalOutlined from '@mui/icons-material/LocalHospitalOutlined';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import RepeatOutlined from '@mui/icons-material/RepeatOutlined';
import StickyNote2Outlined from '@mui/icons-material/StickyNote2Outlined';
import TimerOutlined from '@mui/icons-material/TimerOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  InputBase,
  Stack,
  type SvgIconProps,
  TextField,
  Typography,
} from '@mui/material';
import APIService from '@services/APIService';
import { useEventTracking } from '@services/statsig';
import type { FormWithRevisionSummary } from '@store/generated/forms';
import { useAppDispatch, useModal } from '@store/hooks';
import { formatAppointmentDuration } from '@utils/durationUtils';
import {
  type ConcreteVisitModality,
  formatSessionSummary,
  getModalityPlaceOfServiceError,
  getTelehealthForModality,
  normalizeVisitModality,
  type VisitModality,
} from '@utils/modalityPlaceOfService';
import { getPatientPrimaryContactPhone } from '@utils/patientUtils';
import {
  buildSessionBillingPayload,
  getSessionBillingCptCodes,
  getSessionBillingPlaceOfService,
  type SessionBillingPayload,
} from '@utils/sessionBilling';
import { capitalize } from '@utils/utils';
import { addMinutes, format, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { RRule, rrulestr } from 'rrule';

import type { Patient as PatientRecord } from '../../../../types/types';
import { useOrgScheduleAccess } from '../../hooks/useOrgScheduleAccess';
import { useProviderNameMap } from '../../hooks/useProviderNameMap';
import {
  clearOptimisticAppointmentPatch,
  setOptimisticAppointmentPatch,
  useReassignAppointmentMutation,
  useTransitionAppointmentStatusMutation,
  useUpdateAppointmentMutation,
  useUpdateAppointmentSeriesMutation,
  useUpdateAppointmentVisitStatusMutation,
} from '../../store';
import { getVisitStatusDisplay, VISIT_STATUSES, type VisitStatus } from '../../theme/visitStatus';
import type { Appointment, AppointmentUpdate } from '../../types';
import {
  customRecurrenceRuleToRRule,
  rRuleToCustomRecurrenceRule,
} from '../../utils/recurrenceRule';
import CustomRecurrenceModal, { type CustomRecurrenceRule } from '../CustomRecurrenceModal';
import {
  EditorOptionList,
  PropertyEditorSearchFrame as SharedPropertyEditorSearchFrame,
  type PropertyEditorSearchFrameProps,
} from '../shared/PropertyEditorSearchFrame';
import { SessionDetailsFields } from '../shared/SessionDetailsFields';

type AppointmentUpdateWithSessionBilling = AppointmentUpdate & {
  session_billing?: SessionBillingPayload | null;
};

type AppointmentOptimisticPatch = Partial<Appointment> & {
  session_billing?: SessionBillingPayload | null;
};

// The appointment's date + collapsed time range, e.g. "Thu Jun 18 · 10:00 - 10:30 AM"
// (the meridiem collapses onto the end when start and end share it, "11:30 AM -
// 12:30 PM" across noon). Falls back to just the start time when there's no duration.
const formatDateTimeWithRange = (
  start: Date,
  durationMinutes: number | null | undefined,
): string => {
  if (!durationMinutes || durationMinutes <= 0) {
    return format(start, 'EEE MMM d · h:mm a');
  }
  const end = addMinutes(start, durationMinutes);
  const sameMeridiem = format(start, 'a') === format(end, 'a');
  const startTime = format(start, sameMeridiem ? 'h:mm' : 'h:mm a');
  const endTime = format(end, 'h:mm a');
  return `${format(start, 'EEE MMM d')} · ${startTime} - ${endTime}`;
};

const COMMON_DURATIONS = [15, 20, 30, 45, 50, 60] as const;
const DEFAULT_FORM_OFFSET = '-P1D';
const DEFAULT_REMINDER_OFFSET = '-P1D';
const PATIENT_EDITOR_PAGE_SIZE = 8;

const FORM_TIMING_PRESETS = [
  { display: '2 hours before appointment', iso: '-PT2H' },
  { display: '1 day before appointment', iso: '-P1D' },
  { display: '1 week before appointment', iso: '-P7D' },
  { display: '5 minutes after appointment', iso: 'PT5M' },
] as const;

const REMINDER_TIMING_PRESETS = [
  { display: '2 hours before appointment', iso: '-PT2H' },
  { display: '1 day before appointment', iso: '-P1D' },
  { display: '1 week before appointment', iso: '-P7D' },
] as const;

const APPOINTMENT_REMINDER = {
  id: 'appointment-reminder',
  reminderTemplateId: 'xxx',
  title: 'Appointment Reminder',
} as const;

const RECURRENCE_PRESETS = [
  { label: 'One-time', value: 'none' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Every 2 weeks', value: 'biweekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' },
] as const;

type AppointmentFormRule = NonNullable<AppointmentUpdate['form_rules']>[number];
type AppointmentReminderRule = NonNullable<AppointmentUpdate['reminder_rules']>[number];
type RecurrencePresetValue = (typeof RECURRENCE_PRESETS)[number]['value'] | 'custom';
type TimingPreset = { display: string; iso: string };
type DisplayAppointmentStatus = 'scheduled' | 'confirmed' | 'cancelled' | 'reschedule_requested';

interface AttachedRuleItem {
  id: string;
  title: string;
  offset: string;
  timing: string;
  formDefinitionId?: string;
  reminderTemplateId?: string;
  ruleId?: string | null;
}

export type AppointmentPropertyRowKey =
  | 'status'
  | 'visit_status'
  | 'provider'
  | 'patient'
  | 'type'
  | 'labels'
  | 'telehealth'
  | 'time'
  | 'duration'
  | 'recurrence'
  | 'forms'
  | 'reminders'
  | 'notes';

interface AppointmentPropertiesTableProps {
  appointment: Appointment;
  rows?: AppointmentPropertyRowKey[];
  interactive?: boolean;
  interactiveRows?: AppointmentPropertyRowKey[];
  editorMode?: 'floating' | 'inline';
  onEditorInteraction?: () => void;
}

export interface AppointmentCreatePropertiesTableProps {
  mode: 'create';
  providerId?: string;
  showProvider?: boolean;
  onProviderSelect?: (providerId: string) => void;
  patient?: PatientRecord;
  onPatientSelect: (patient: PatientRecord | undefined) => void;
  appointmentType?: AppointmentType;
  /** Current type was auto-applied (provider's most-used), not user-picked (GDN-2228). */
  appointmentTypeSuggested?: boolean;
  onAppointmentTypeSelect: (appointmentType: AppointmentType | undefined) => void;
  date: string;
  startTime: string;
  duration: number;
  recurrence: string;
  onDateChange: (date: string) => void;
  onStartTimeChange: (time: string) => void;
  onDurationChange: (duration: number) => void;
  onRecurrenceChange: (value: string) => void;
  onCustomRecurrenceClick?: () => void;
  telehealth: boolean;
  onTelehealthChange: (value: boolean) => void;
  modality?: VisitModality;
  onModalityChange?: (value: ConcreteVisitModality | null) => void;
  placeOfService?: string | null;
  onPlaceOfServiceChange?: (value: string | null) => void;
  cptCodes?: string[];
  onCptCodesChange?: (codes: string[]) => void;
  useJotmeet?: boolean;
  onUseJotmeetChange?: (value: boolean) => void;
  sessionError?: string | null;
  forms: ExpandableFieldItem[];
  reminders: ExpandableFieldItem[];
  onFormsChange: (forms: ExpandableFieldItem[]) => void;
  onRemindersChange: (reminders: ExpandableFieldItem[]) => void;
  notes?: string;
  onNotesChange?: (notes: string) => void;
  autoFocusPatientPicker?: boolean;
  editorMode?: 'floating' | 'inline';
  organizationId?: string | null;
  labelIds?: string[];
  onLabelIdsChange?: (labelIds: string[]) => void;
}

const AppointmentPropertyEditorInteractionContext = createContext<(() => void) | null>(null);

const APPOINTMENT_PROPERTY_ROW_ORDER: AppointmentPropertyRowKey[] = [
  'status',
  'visit_status',
  'provider',
  'patient',
  'type',
  'labels',
  'telehealth',
  'time',
  'duration',
  'recurrence',
  'forms',
  'reminders',
  'notes',
];

const renderAppointmentPropertyRows = (
  renderRow: (key: AppointmentPropertyRowKey) => React.ReactNode,
) => (
  <>
    {APPOINTMENT_PROPERTY_ROW_ORDER.map(key => (
      <React.Fragment key={key}>{renderRow(key)}</React.Fragment>
    ))}
  </>
);

export const AppointmentPropertiesTable = (
  props: AppointmentPropertiesTableProps | AppointmentCreatePropertiesTableProps,
) => {
  if (!('appointment' in props)) {
    return <AppointmentCreatePropertiesTable {...props} />;
  }

  return <ExistingAppointmentPropertiesTable {...props} />;
};

const ExistingAppointmentPropertiesTable = ({
  appointment,
  rows,
  interactive = false,
  interactiveRows,
  editorMode = 'floating',
  onEditorInteraction,
}: AppointmentPropertiesTableProps) => {
  const showRow = (key: AppointmentPropertyRowKey) => !rows || rows.includes(key);
  const isRowInteractive = (key: AppointmentPropertyRowKey) =>
    interactive || interactiveRows?.includes(key) === true;
  const { hasOrgScheduleAccess } = useOrgScheduleAccess();
  const { clinicians, isLoading: isLoadingClinicians } = useClinicians({
    skip: !hasOrgScheduleAccess,
  });
  const dispatch = useAppDispatch();
  const providerNameMap = useProviderNameMap();
  const { enqueueSnackbar } = useSnackbar();
  const [updateAppointment, { isLoading: isUpdatingAppointment }] = useUpdateAppointmentMutation();
  const [updateAppointmentSeries, { isLoading: isUpdatingSeries }] =
    useUpdateAppointmentSeriesMutation();
  const [optimisticType, setOptimisticType] = useState<{
    id: string | null;
    label: string | null;
  } | null>(null);
  const [isCustomRecurrenceModalOpen, setIsCustomRecurrenceModalOpen] = useState(false);
  useEffect(() => {
    setOptimisticType(null);
  }, [appointment.appointment_id]);
  const providerName =
    getAppointmentProviderName(appointment) ??
    providerNameMap.get(appointment.provider_id ?? '') ??
    lookupProviderName(appointment.provider_id, clinicians);
  const visitStatus = getVisitStatusDisplay(
    appointment.no_showed ? 'no_show' : appointment.visit_status,
  );
  const status = getStatusDisplay(appointment);
  const start = new Date(appointment.start_time);
  const forms = mapFormRules(appointment.form_rules);
  const reminders = mapReminderRules(appointment.reminder_rules);
  const typeLabel =
    optimisticType?.label ?? appointment.encounter_type ?? appointment.title ?? null;
  const appointmentProviderId =
    appointment.provider_id ?? appointment.provider?.user_id ?? undefined;
  const recurrenceValue = getRecurrencePresetValue(appointment.series?.rrule);
  const isSavingRecurrence = isUpdatingAppointment || isUpdatingSeries;
  const isRecurrenceInteractive = isRowInteractive('recurrence');
  // Seed the custom-recurrence modal from the existing series so re-opening the
  // editor shows the current pattern instead of resetting to defaults.
  const initialCustomRecurrenceRule = useMemo(
    () =>
      appointment.series?.rrule ? rRuleToCustomRecurrenceRule(appointment.series.rrule) : null,
    [appointment.series?.rrule],
  );
  const appointmentModality: VisitModality =
    normalizeVisitModality(appointment.modality) ?? (appointment.telehealth ? 'video' : null);
  const appointmentPlaceOfService = getSessionBillingPlaceOfService(appointment);
  const appointmentCptCodes = getSessionBillingCptCodes(appointment);
  const appointmentSessionError = getModalityPlaceOfServiceError({
    modality: appointmentModality,
    placeOfService: appointmentPlaceOfService,
  });
  const persistAppointmentUpdate = (
    updates: AppointmentUpdateWithSessionBilling,
    optimisticAppointmentPatch?: AppointmentOptimisticPatch,
  ) => {
    dispatch(
      setOptimisticAppointmentPatch({
        appointmentId: appointment.appointment_id,
        patch: {
          ...updates,
          ...optimisticAppointmentPatch,
        } as Partial<Appointment>,
      }),
    );

    return updateAppointment({
      appointmentId: appointment.appointment_id,
      appointmentUpdate: updates,
    })
      .unwrap()
      .catch((error: unknown) => {
        dispatch(clearOptimisticAppointmentPatch(appointment.appointment_id));
        throw error;
      });
  };
  const persistRecurrenceUpdate = (value: RecurrencePresetValue) => {
    if (value === 'custom') return Promise.resolve();

    if (value === 'none') {
      if (appointment.series?.series_id) {
        return persistAppointmentUpdate(
          { detach_from_series: true },
          { series_id: null, series: null },
        );
      }
      return Promise.resolve();
    }

    const rrule = getRRuleForRecurrencePreset(value);
    if (!rrule) return Promise.resolve();

    if (appointment.series?.series_id) {
      return updateAppointmentSeries({
        seriesId: appointment.series.series_id,
        appointmentSeriesUpdate: { rrule },
      }).unwrap();
    }

    return persistAppointmentUpdate({ rrule, create_series: true });
  };
  const handleUpdateFailure = (label: string, error: unknown) => {
    console.error(`Failed to update appointment ${label}:`, error);
    enqueueSnackbar(`Failed to update ${label}. Please try again.`, { variant: 'error' });
  };
  const handleCustomRecurrenceSave = (rule: CustomRecurrenceRule) => {
    const result = customRecurrenceRuleToRRule(rule);
    if (!result) {
      handleUpdateFailure('recurrence', new Error('Invalid custom recurrence rule'));
      return;
    }
    const { rrule } = result;
    const promise = appointment.series?.series_id
      ? updateAppointmentSeries({
          seriesId: appointment.series.series_id,
          appointmentSeriesUpdate: { rrule },
        }).unwrap()
      : persistAppointmentUpdate({ rrule, create_series: true });
    void promise.catch((error: unknown) => handleUpdateFailure('recurrence', error));
  };
  const persistSessionUpdate = (
    updates: AppointmentUpdateWithSessionBilling,
    optimisticAppointmentPatch: AppointmentOptimisticPatch,
  ) => {
    const nextModality = 'modality' in updates ? updates.modality : appointmentModality;
    const nextPlaceOfService =
      updates.session_billing !== undefined
        ? (updates.session_billing?.place_of_service ?? null)
        : appointmentPlaceOfService;
    const error = getModalityPlaceOfServiceError({
      modality: nextModality,
      placeOfService: nextPlaceOfService,
    });

    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
      return;
    }

    const locksDefaults = 'modality' in updates || 'session_billing' in updates;

    void persistAppointmentUpdate(
      {
        ...updates,
        ...(locksDefaults && { override_defaults: true }),
      },
      optimisticAppointmentPatch,
    ).catch((updateError: unknown) => handleUpdateFailure('session details', updateError));
  };

  return (
    <AppointmentPropertyEditorInteractionContext.Provider value={onEditorInteraction ?? null}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {showRow('status') &&
          (isRowInteractive('status') ? (
            <StatusPropertyRow appointment={appointment} editorMode={editorMode} />
          ) : (
            <PropertyRow icon={CheckCircleOutlined} label="Status">
              <StatusValue color={status.color} label={status.label} />
            </PropertyRow>
          ))}

        {showRow('visit_status') &&
          (isRowInteractive('visit_status') ? (
            <VisitStatusPropertyRow appointment={appointment} editorMode={editorMode} />
          ) : (
            <PropertyRow icon={visitStatus.icon} label="Visit status">
              <DotValue color={visitStatus.color} label={visitStatus.label} />
            </PropertyRow>
          ))}

        {showRow('provider') && (
          <ProviderPropertyRow
            appointment={appointment}
            providerName={providerName}
            clinicians={clinicians}
            isLoadingClinicians={isLoadingClinicians}
            disabled={!isRowInteractive('provider') || !hasOrgScheduleAccess}
            editorMode={editorMode}
          />
        )}

        {showRow('patient') && (
          <PatientPropertyRow
            appointment={appointment}
            disabled={!isRowInteractive('patient')}
            editorMode={editorMode}
            onUpdate={persistAppointmentUpdate}
          />
        )}

        {showRow('type') && (
          <EditablePropertyRow
            icon={LocalHospitalOutlined}
            label="Type"
            disabled={!isRowInteractive('type')}
            editorMode={editorMode}
            value={
              typeLabel ? (
                <PlainValue>{typeLabel}</PlainValue>
              ) : (
                <EmptyValue>Set type...</EmptyValue>
              )
            }
            popoverWidth={280}
            editor={({ onClose }) => (
              <AppointmentTypeEditor
                value={appointment.encounter_type_id ?? null}
                providerId={appointmentProviderId}
                isSaving={isUpdatingAppointment}
                onChange={(updates, selectedType) => {
                  const previousType = optimisticType;
                  const selectedTypeModality = selectedType.default_modality ?? null;
                  const selectedTypeUseJotmeet = Boolean(
                    selectedType.default_use_jotmeet && selectedTypeModality === 'video',
                  );
                  const selectedTypeSessionBilling = buildSessionBillingPayload({
                    placeOfService: selectedType.default_place_of_service ?? null,
                    cptCodes: selectedType.default_cpt_codes,
                  });
                  setOptimisticType({
                    id: selectedType.encounter_type_id ?? null,
                    label: selectedType.encounter_type,
                  });

                  void persistAppointmentUpdate(updates, {
                    encounter_type_id: selectedType.encounter_type_id ?? null,
                    encounter_type: selectedType.encounter_type,
                    title: selectedType.encounter_type,
                    color: selectedType.color,
                    ...(selectedType.default_duration
                      ? { duration: selectedType.default_duration }
                      : {}),
                    ...(selectedType.telehealth != null
                      ? { telehealth: selectedType.telehealth }
                      : {}),
                    modality: selectedTypeModality,
                    session_billing: selectedTypeSessionBilling,
                    use_jotmeet: selectedTypeUseJotmeet,
                  })
                    .then(onClose)
                    .catch((error: unknown) => {
                      setOptimisticType(previousType);
                      console.error('Failed to update appointment type:', error);
                      enqueueSnackbar('Failed to update appointment type. Please try again.', {
                        variant: 'error',
                      });
                    });
                }}
              />
            )}
          />
        )}

        {showRow('labels') && (
          <LabelsPropertyRow
            appointment={appointment}
            disabled={!isRowInteractive('labels')}
            editorMode={editorMode}
            onUpdate={persistAppointmentUpdate}
          />
        )}

        {showRow('telehealth') && (
          <EditablePropertyRow
            icon={VideocamOutlined}
            label="Session"
            disabled={!isRowInteractive('telehealth')}
            editorMode={editorMode}
            popoverWidth={380}
            value={
              <PlainValue>
                {formatSessionSummary({
                  modality: appointmentModality,
                  placeOfService: appointmentPlaceOfService,
                  cptCodes: appointmentCptCodes,
                  useJotmeet: appointment.use_jotmeet,
                })}
              </PlainValue>
            }
            editor={() => (
              <SessionDetailsFields
                compact
                disabled={isUpdatingAppointment}
                modality={appointmentModality}
                placeOfService={appointmentPlaceOfService}
                cptCodes={appointmentCptCodes}
                useJotmeet={appointment.use_jotmeet}
                error={appointmentSessionError}
                onModalityChange={modality => {
                  const nextUseJotmeet = modality === 'video' ? appointment.use_jotmeet : false;
                  const sessionBilling = buildSessionBillingPayload({
                    placeOfService: appointmentPlaceOfService,
                    cptCodes: appointmentCptCodes,
                  });
                  persistSessionUpdate(
                    {
                      modality,
                      telehealth: getTelehealthForModality(modality),
                      session_billing: sessionBilling,
                      use_jotmeet: Boolean(nextUseJotmeet),
                    },
                    {
                      modality,
                      telehealth: getTelehealthForModality(modality),
                      session_billing: sessionBilling,
                      use_jotmeet: Boolean(nextUseJotmeet),
                    },
                  );
                }}
                onPlaceOfServiceChange={placeOfService => {
                  const sessionBilling = buildSessionBillingPayload({
                    placeOfService,
                    cptCodes: appointmentCptCodes,
                  });
                  persistSessionUpdate(
                    { session_billing: sessionBilling },
                    { session_billing: sessionBilling },
                  );
                }}
                onCptCodesChange={cptCodes => {
                  const sessionBilling = buildSessionBillingPayload({
                    placeOfService: appointmentPlaceOfService,
                    cptCodes,
                  });
                  persistSessionUpdate(
                    { session_billing: sessionBilling },
                    { session_billing: sessionBilling },
                  );
                }}
                onUseJotmeetChange={useJotmeet =>
                  persistSessionUpdate({ use_jotmeet: useJotmeet }, { use_jotmeet: useJotmeet })
                }
              />
            )}
          />
        )}

        {showRow('time') && (
          <EditablePropertyRow
            icon={EventOutlined}
            label="Date/time"
            disabled={!isRowInteractive('time')}
            editorMode={editorMode}
            value={<PlainValue>{formatDateTimeWithRange(start, appointment.duration)}</PlainValue>}
            popoverWidth={280}
            editor={({ onClose }) => (
              <TimeEditor
                value={appointment.start_time}
                isSaving={isUpdatingAppointment}
                onClose={onClose}
                onChange={startTime => {
                  void persistAppointmentUpdate({ start_time: startTime }).catch((error: unknown) =>
                    handleUpdateFailure('date/time', error),
                  );
                }}
              />
            )}
          />
        )}

        {showRow('duration') && (
          <EditablePropertyRow
            icon={TimerOutlined}
            label="Duration"
            disabled={!isRowInteractive('duration')}
            editorMode={editorMode}
            value={<PlainValue>{appointment.duration} min</PlainValue>}
            popoverWidth={260}
            editor={({ onClose }) => (
              <DurationEditor
                value={appointment.duration}
                startTime={appointment.start_time}
                isSaving={isUpdatingAppointment}
                onChange={duration => {
                  void persistAppointmentUpdate({ duration }).catch((error: unknown) =>
                    handleUpdateFailure('duration', error),
                  );
                }}
                onClose={onClose}
              />
            )}
          />
        )}

        {showRow('recurrence') && (
          <EditablePropertyRow
            icon={RepeatOutlined}
            label="Recurrence"
            disabled={!isRowInteractive('recurrence')}
            editorMode={editorMode}
            value={
              appointment.series?.rrule ? (
                <PlainValue>{getRecurrenceText(appointment.series.rrule)}</PlainValue>
              ) : (
                <EmptyValue>One-time</EmptyValue>
              )
            }
            popoverWidth={240}
            editor={({ onClose }) => (
              <RecurrenceEditor
                value={recurrenceValue}
                isSaving={isSavingRecurrence}
                includeCustomOption={isRecurrenceInteractive}
                onChange={value => {
                  if (value === 'custom') {
                    setIsCustomRecurrenceModalOpen(true);
                    onClose();
                    return;
                  }
                  void persistRecurrenceUpdate(value)
                    .then(onClose)
                    .catch((error: unknown) => handleUpdateFailure('recurrence', error));
                }}
              />
            )}
          />
        )}

        {showRow('forms') && (
          <EditablePropertyRow
            icon={AssignmentOutlined}
            label="Forms"
            disabled={!isRowInteractive('forms')}
            editorMode={editorMode}
            value={<ChipList items={forms} emptyLabel="No forms" />}
            popoverWidth={320}
            editor={() => (
              <FormsEditor
                value={forms}
                isSaving={isUpdatingAppointment}
                onChange={nextForms => {
                  void persistAppointmentUpdate({
                    form_rules: toFormRules(nextForms),
                    reminder_rules: toReminderRules(reminders),
                    override_defaults: true,
                  }).catch((error: unknown) => handleUpdateFailure('forms', error));
                }}
              />
            )}
          />
        )}

        {showRow('reminders') && (
          <EditablePropertyRow
            icon={NotificationsOutlined}
            label="Reminders"
            disabled={!isRowInteractive('reminders')}
            editorMode={editorMode}
            value={<ChipList items={reminders} emptyLabel="No reminders" />}
            popoverWidth={280}
            editor={() => (
              <RemindersEditor
                value={reminders}
                isSaving={isUpdatingAppointment}
                onChange={nextReminders => {
                  void persistAppointmentUpdate({
                    form_rules: toFormRules(forms),
                    reminder_rules: toReminderRules(nextReminders),
                    override_defaults: true,
                  }).catch((error: unknown) => handleUpdateFailure('reminders', error));
                }}
              />
            )}
          />
        )}

        {showRow('notes') && (
          <EditablePropertyRow
            icon={StickyNote2Outlined}
            label="Notes"
            disabled={!isRowInteractive('notes')}
            alignTop
            editorMode={editorMode}
            popoverWidth={340}
            value={
              appointment.notes ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 13,
                    color: ELIG_TOKENS.ink,
                    fontStyle: 'italic',
                    lineHeight: 1.4,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '8.4em',
                    overflowY: 'auto',
                  }}
                >
                  {appointment.notes}
                </Typography>
              ) : (
                <EmptyValue>Add a note...</EmptyValue>
              )
            }
            editor={() => (
              <NotesEditor
                value={appointment.notes ?? ''}
                isSaving={isUpdatingAppointment}
                onSave={notes => {
                  return persistAppointmentUpdate({ notes }).catch((error: unknown) => {
                    handleUpdateFailure('notes', error);
                    throw error;
                  });
                }}
              />
            )}
          />
        )}
      </Box>
      <CustomRecurrenceModal
        open={isCustomRecurrenceModalOpen}
        onClose={() => setIsCustomRecurrenceModalOpen(false)}
        onSave={handleCustomRecurrenceSave}
        initialRule={initialCustomRecurrenceRule ?? undefined}
      />
    </AppointmentPropertyEditorInteractionContext.Provider>
  );
};

const AppointmentCreatePropertiesTable = ({
  providerId,
  showProvider = false,
  onProviderSelect,
  patient,
  onPatientSelect,
  appointmentType,
  appointmentTypeSuggested = false,
  onAppointmentTypeSelect,
  date,
  startTime,
  duration,
  recurrence,
  onDateChange,
  onStartTimeChange,
  onDurationChange,
  onRecurrenceChange,
  onCustomRecurrenceClick,
  telehealth,
  onTelehealthChange,
  modality,
  onModalityChange,
  placeOfService,
  onPlaceOfServiceChange,
  cptCodes = [],
  onCptCodesChange,
  useJotmeet = false,
  onUseJotmeetChange,
  sessionError,
  forms,
  reminders,
  onFormsChange,
  onRemindersChange,
  notes,
  onNotesChange,
  autoFocusPatientPicker = false,
  editorMode = 'floating',
  organizationId,
  labelIds,
  onLabelIdsChange,
}: AppointmentCreatePropertiesTableProps) => {
  const subjectLanguage = useSubjectLanguage();
  const subjectLanguageLabel = capitalize(subjectLanguage);
  const { clinicians, isLoading: isLoadingClinicians } = useClinicians({ skip: !showProvider });
  const startIso = toCreateStartIso(date, startTime);
  const providerName =
    clinicians.find(clinician => clinician.userId === providerId)?.name ??
    (providerId ? 'Assigned provider' : null);
  const createModality: VisitModality = modality ?? (telehealth ? 'video' : null);
  const formItems = forms.map(expandableItemToAttachedRuleItem);
  const reminderItems = reminders.map(expandableItemToAttachedRuleItem);
  const labelsEnabled = useAppointmentLabelsEnabled();
  const { data: createLabelsData } = useGetLabelsQuery(
    { orgId: organizationId ?? '' },
    { skip: !organizationId || !labelsEnabled },
  );
  const selectedCreateLabels = (labelIds ?? [])
    .map(id => (createLabelsData?.items ?? []).find(label => label.label_id === id))
    .filter((label): label is NonNullable<typeof label> => Boolean(label))
    .map(label => ({
      label_id: label.label_id,
      label_group_id: label.label_group_id,
      name: label.name,
      color: label.color,
    }));

  return (
    <AppointmentPropertyEditorInteractionContext.Provider value={null}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {renderAppointmentPropertyRows(key => {
          switch (key) {
            case 'provider':
              return showProvider && onProviderSelect ? (
                <EditablePropertyRow
                  icon={PersonOutlined}
                  label="Provider"
                  editorMode={editorMode}
                  popoverWidth={260}
                  value={
                    providerName ? (
                      <ProviderValue name={providerName} />
                    ) : (
                      <EmptyValue>Assign provider</EmptyValue>
                    )
                  }
                  editor={({ onClose }) => (
                    <ProviderEditor
                      value={providerId ?? null}
                      clinicians={clinicians}
                      isLoading={isLoadingClinicians}
                      isSaving={false}
                      onChange={nextProviderId => {
                        if (nextProviderId) onProviderSelect(nextProviderId);
                        onClose();
                      }}
                    />
                  )}
                />
              ) : null;
            case 'patient':
              return (
                <EditablePropertyRow
                  icon={PersonOutlined}
                  label={subjectLanguageLabel}
                  editorMode={editorMode}
                  popoverWidth={320}
                  value={
                    patient ? (
                      <PatientValue patient={patient} />
                    ) : (
                      <EmptyValue>Set {subjectLanguage}...</EmptyValue>
                    )
                  }
                  editor={({ onClose }) => (
                    <PatientEditor
                      patient={patient}
                      autoFocus={autoFocusPatientPicker}
                      onChange={nextPatient => {
                        onPatientSelect(nextPatient);
                        onClose();
                      }}
                    />
                  )}
                />
              );
            case 'type':
              return (
                <EditablePropertyRow
                  icon={LocalHospitalOutlined}
                  label="Type"
                  editorMode={editorMode}
                  popoverWidth={280}
                  value={
                    appointmentType?.encounter_type ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <PlainValue>{appointmentType.encounter_type}</PlainValue>
                        {appointmentTypeSuggested && (
                          <JotChip
                            label="Suggested"
                            color="info"
                            variant="soft"
                            size="xSmall"
                            title="Auto-selected from the provider's most-used appointment types"
                          />
                        )}
                      </Box>
                    ) : (
                      <EmptyValue>Set type...</EmptyValue>
                    )
                  }
                  editor={({ onClose }) => (
                    <AppointmentTypeEditor
                      value={appointmentType?.encounter_type_id ?? null}
                      providerId={providerId}
                      isSaving={false}
                      onChange={(_, selectedType) => {
                        onAppointmentTypeSelect(selectedType);
                        onClose();
                      }}
                    />
                  )}
                />
              );
            case 'labels':
              // Shown for single appointments and recurring series alike — for a
              // series the chosen labels become its default_label_ids and
              // propagate to each materialized occurrence (GDN-1968).
              if (!labelsEnabled || !organizationId) {
                return null;
              }
              return (
                <EditablePropertyRow
                  icon={LabelOutlined}
                  label="Labels"
                  alignTop
                  editorMode={editorMode}
                  popoverWidth={300}
                  value={
                    selectedCreateLabels.length > 0 ? (
                      <LabelChipRow labels={selectedCreateLabels} />
                    ) : (
                      <EmptyValue>No labels</EmptyValue>
                    )
                  }
                  editor={() => (
                    <LabelPicker
                      organizationId={organizationId}
                      value={labelIds ?? []}
                      onChange={ids => onLabelIdsChange?.(ids)}
                      autoFocus
                      trackingContext={{
                        scope: 'appointment',
                        surface: 'appointment_create',
                      }}
                    />
                  )}
                />
              );
            case 'telehealth':
              return (
                <EditablePropertyRow
                  icon={VideocamOutlined}
                  label="Session"
                  editorMode={editorMode}
                  popoverWidth={380}
                  value={
                    <PlainValue>
                      {formatSessionSummary({
                        modality: createModality,
                        placeOfService,
                        cptCodes,
                        useJotmeet,
                      })}
                    </PlainValue>
                  }
                  editor={() => (
                    <SessionDetailsFields
                      compact
                      modality={createModality}
                      placeOfService={placeOfService}
                      cptCodes={cptCodes}
                      useJotmeet={useJotmeet}
                      error={sessionError}
                      onModalityChange={value => {
                        if (onModalityChange) {
                          onModalityChange(value);
                        } else {
                          onTelehealthChange(value === 'video' || value === 'audio');
                        }
                      }}
                      onPlaceOfServiceChange={value => onPlaceOfServiceChange?.(value)}
                      onCptCodesChange={value => onCptCodesChange?.(value)}
                      onUseJotmeetChange={onUseJotmeetChange}
                    />
                  )}
                />
              );
            case 'time':
              return (
                <EditablePropertyRow
                  icon={EventOutlined}
                  label="Date/time"
                  editorMode={editorMode}
                  popoverWidth={280}
                  value={
                    <PlainValue>{formatDateTimeWithRange(new Date(startIso), duration)}</PlainValue>
                  }
                  editor={({ onClose }) => (
                    <TimeEditor
                      value={startIso}
                      isSaving={false}
                      onClose={onClose}
                      onChange={nextStartIso => {
                        const nextDraft = toDateTimeDraft(nextStartIso);
                        onDateChange(nextDraft.date);
                        onStartTimeChange(nextDraft.time);
                      }}
                    />
                  )}
                />
              );
            case 'duration':
              return (
                <EditablePropertyRow
                  icon={TimerOutlined}
                  label="Duration"
                  editorMode={editorMode}
                  popoverWidth={260}
                  value={<PlainValue>{duration} min</PlainValue>}
                  editor={({ onClose }) => (
                    <DurationEditor
                      value={duration}
                      startTime={startIso}
                      isSaving={false}
                      onChange={onDurationChange}
                      onClose={onClose}
                    />
                  )}
                />
              );
            case 'recurrence':
              return (
                <EditablePropertyRow
                  icon={RepeatOutlined}
                  label="Recurrence"
                  editorMode={editorMode}
                  popoverWidth={240}
                  value={
                    recurrence === 'none' ? (
                      <EmptyValue>One-time</EmptyValue>
                    ) : (
                      <PlainValue>{getCreateRecurrenceLabel(recurrence)}</PlainValue>
                    )
                  }
                  editor={({ onClose }) => (
                    <RecurrenceEditor
                      value={coerceRecurrencePresetValue(recurrence)}
                      isSaving={false}
                      includeCustomOption={Boolean(onCustomRecurrenceClick)}
                      onChange={value => {
                        if (value === 'custom') {
                          onCustomRecurrenceClick?.();
                          onClose();
                          return;
                        }

                        onRecurrenceChange(value);
                        onClose();
                      }}
                    />
                  )}
                />
              );
            case 'forms':
              return (
                <EditablePropertyRow
                  icon={AssignmentOutlined}
                  label="Forms"
                  editorMode={editorMode}
                  alignTop
                  popoverWidth={320}
                  value={<ChipList items={formItems} emptyLabel="No forms" />}
                  editor={() => (
                    <FormsEditor
                      value={formItems}
                      isSaving={false}
                      onChange={nextItems =>
                        onFormsChange(nextItems.map(attachedRuleItemToExpandableItem))
                      }
                    />
                  )}
                />
              );
            case 'reminders':
              return (
                <EditablePropertyRow
                  icon={NotificationsOutlined}
                  label="Reminders"
                  editorMode={editorMode}
                  alignTop
                  popoverWidth={280}
                  value={<ChipList items={reminderItems} emptyLabel="No reminders" />}
                  editor={() => (
                    <RemindersEditor
                      value={reminderItems}
                      isSaving={false}
                      onChange={nextItems =>
                        onRemindersChange(nextItems.map(attachedRuleItemToExpandableItem))
                      }
                    />
                  )}
                />
              );
            case 'notes':
              return onNotesChange ? (
                <EditablePropertyRow
                  icon={StickyNote2Outlined}
                  label="Notes"
                  editorMode={editorMode}
                  alignTop
                  popoverWidth={340}
                  value={
                    notes ? (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: 13,
                          color: ELIG_TOKENS.ink,
                          fontStyle: 'italic',
                          lineHeight: 1.4,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          maxHeight: '8.4em',
                          overflowY: 'auto',
                        }}
                      >
                        {notes}
                      </Typography>
                    ) : (
                      <EmptyValue>Add a note...</EmptyValue>
                    )
                  }
                  editor={() => (
                    <NotesEditor
                      value={notes ?? ''}
                      isSaving={false}
                      onSave={nextNotes => {
                        onNotesChange(nextNotes ?? '');
                        return Promise.resolve();
                      }}
                    />
                  )}
                />
              ) : null;
            default:
              return null;
          }
        })}
      </Box>
    </AppointmentPropertyEditorInteractionContext.Provider>
  );
};

const StatusPropertyRow = ({
  appointment,
  editorMode,
}: {
  appointment: Appointment;
  editorMode: 'floating' | 'inline';
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [optimisticStatus, setOptimisticStatus] = useState<DisplayAppointmentStatus | null>(null);
  const [transitionStatus, { isLoading }] = useTransitionAppointmentStatusMutation();
  const value = optimisticStatus ?? getDisplayAppointmentStatus(appointment);
  const display = getStatusDisplayForValue(value, appointment);

  useEffect(() => {
    setOptimisticStatus(null);
  }, [appointment.appointment_id, appointment.status, appointment.confirmation_status]);

  const commit = (nextStatus: DisplayAppointmentStatus, onClose: () => void) => {
    const previous = value;
    const transition = getStatusTransitionForDisplayStatus(appointment, nextStatus);

    setOptimisticStatus(nextStatus);
    dispatch(
      setOptimisticAppointmentPatch({
        appointmentId: appointment.appointment_id,
        patch: getOptimisticPatchForStatusTransition(appointment, transition),
      }),
    );
    onClose();

    void transitionStatus({
      appointmentId: appointment.appointment_id,
      appointmentStatusTransition: {
        ...transition,
        source: 'calendar_status_property',
        scope: 'single',
      },
    })
      .unwrap()
      .catch((error: unknown) => {
        setOptimisticStatus(previous);
        dispatch(clearOptimisticAppointmentPatch(appointment.appointment_id));
        console.error('Failed to update appointment status:', error);
        enqueueSnackbar('Failed to update status. Please try again.', { variant: 'error' });
      });
  };

  return (
    <EditablePropertyRow
      icon={CheckCircleOutlined}
      label="Status"
      editorMode={editorMode}
      popoverWidth={240}
      value={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <StatusValue color={display.color} label={display.label} />
          {isLoading && <CircularProgress size={12} />}
        </Box>
      }
      editor={({ onClose }) => (
        <StatusEditor
          value={value}
          isSaving={isLoading}
          onChange={nextStatus => commit(nextStatus, onClose)}
        />
      )}
    />
  );
};

const VisitStatusPropertyRow = ({
  appointment,
  editorMode,
}: {
  appointment: Appointment;
  editorMode: 'floating' | 'inline';
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [optimisticStatus, setOptimisticStatus] = useState<VisitStatus | null>(null);
  const [updateVisitStatus, { isLoading }] = useUpdateAppointmentVisitStatusMutation();
  const value = optimisticStatus ?? coerceVisitStatus(appointment.visit_status);
  const display = getVisitStatusDisplay(appointment.no_showed ? 'no_show' : value);

  const commit = (status: VisitStatus, onClose: () => void) => {
    const previous = value;
    setOptimisticStatus(status);
    dispatch(
      setOptimisticAppointmentPatch({
        appointmentId: appointment.appointment_id,
        patch: {
          visit_status: status,
          visit_status_changed_at: new Date().toISOString(),
        },
      }),
    );
    onClose();
    void updateVisitStatus({
      appointmentId: appointment.appointment_id,
      appointmentVisitStatusUpdate: { visit_status: status },
    })
      .unwrap()
      .catch((error: unknown) => {
        setOptimisticStatus(previous);
        dispatch(clearOptimisticAppointmentPatch(appointment.appointment_id));
        console.error('Failed to update appointment visit status:', error);
        enqueueSnackbar('Failed to update visit status. Please try again.', { variant: 'error' });
      });
  };

  return (
    <EditablePropertyRow
      icon={display.icon}
      label="Visit status"
      editorMode={editorMode}
      popoverWidth={220}
      value={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <DotValue color={display.color} label={display.label} />
          {isLoading && <CircularProgress size={12} />}
        </Box>
      }
      editor={({ onClose }) => (
        <VisitStatusEditor
          value={value}
          isSaving={isLoading}
          onChange={status => commit(status, onClose)}
        />
      )}
    />
  );
};

const VisitStatusEditor = ({
  value,
  isSaving,
  onChange,
}: {
  value: VisitStatus;
  isSaving: boolean;
  onChange: (value: VisitStatus) => void;
}) => (
  <EditorOptionList>
    {VISIT_STATUSES.map(status => {
      const option = getVisitStatusDisplay(status);
      return (
        <OptionRow
          key={status}
          selected={value === status}
          disabled={isSaving}
          onClick={() => onChange(status)}
        >
          <DotValue color={option.color} label={option.label} />
        </OptionRow>
      );
    })}
  </EditorOptionList>
);

const ProviderPropertyRow = ({
  appointment,
  providerName,
  clinicians,
  isLoadingClinicians,
  disabled,
  editorMode,
}: {
  appointment: Appointment;
  providerName: string | null;
  clinicians: Array<{ userId: string; name: string }>;
  isLoadingClinicians: boolean;
  disabled: boolean;
  editorMode: 'floating' | 'inline';
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [optimisticProviderId, setOptimisticProviderId] = useState<string | null | undefined>();
  const [optimisticProviderName, setOptimisticProviderName] = useState<string | null | undefined>();
  const [reassignAppointment, { isLoading: isReassigning }] = useReassignAppointmentMutation();
  const { orgSchedule: orgScheduleTracking } = useEventTracking();
  const currentProviderId =
    optimisticProviderId !== undefined ? optimisticProviderId : (appointment.provider_id ?? null);
  const currentProviderName =
    optimisticProviderName !== undefined ? optimisticProviderName : providerName;

  const commit = (providerId: string | null, onClose: () => void) => {
    const previousProviderId = currentProviderId;
    const previousProviderName = currentProviderName;
    const nextProviderName =
      providerId === null
        ? null
        : (clinicians.find(clinician => clinician.userId === providerId)?.name ??
          'Assigned provider');

    setOptimisticProviderId(providerId);
    setOptimisticProviderName(nextProviderName);
    dispatch(
      setOptimisticAppointmentPatch({
        appointmentId: appointment.appointment_id,
        patch: {
          provider_id: providerId,
          provider: null,
        },
      }),
    );
    onClose();

    void reassignAppointment({
      appointmentId: appointment.appointment_id,
      appointmentReassign: { provider_id: providerId },
    })
      .unwrap()
      .then(() => {
        orgScheduleTracking.providerReassigned({
          appointment_id: appointment.appointment_id,
          previous_provider_id: appointment.provider_id ?? null,
          new_provider_id: providerId,
        });
      })
      .catch((error: unknown) => {
        setOptimisticProviderId(previousProviderId);
        setOptimisticProviderName(previousProviderName);
        dispatch(clearOptimisticAppointmentPatch(appointment.appointment_id));
        console.error('Failed to update appointment provider:', error);
        enqueueSnackbar('Failed to update provider. Please try again.', { variant: 'error' });
      });
  };

  return (
    <EditablePropertyRow
      icon={PersonOutlined}
      label="Provider"
      disabled={disabled}
      editorMode={editorMode}
      popoverWidth={260}
      value={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {currentProviderName ? (
            <ProviderValue name={currentProviderName} />
          ) : (
            <EmptyValue>Unassigned</EmptyValue>
          )}
          {isReassigning && <CircularProgress size={12} />}
        </Box>
      }
      editor={({ onClose }) => (
        <ProviderEditor
          value={currentProviderId}
          clinicians={clinicians}
          isLoading={isLoadingClinicians}
          isSaving={isReassigning}
          onChange={providerId => commit(providerId, onClose)}
        />
      )}
    />
  );
};

const PatientPropertyRow = ({
  appointment,
  disabled,
  editorMode,
  onUpdate,
}: {
  appointment: Appointment;
  disabled: boolean;
  editorMode: 'floating' | 'inline';
  onUpdate: (
    updates: AppointmentUpdate,
    optimisticAppointmentPatch?: Partial<Appointment>,
  ) => Promise<Appointment>;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [optimisticPatient, setOptimisticPatient] = useState<Appointment['patient'] | undefined>();
  const currentPatient = optimisticPatient !== undefined ? optimisticPatient : appointment.patient;

  useEffect(() => {
    setOptimisticPatient(undefined);
  }, [appointment.appointment_id]);

  const commit = (patient: PatientRecord | undefined, onClose: () => void) => {
    const previousPatient = currentPatient;
    const nextPatient = mapPatientRecordToAppointmentPatient(patient);

    setOptimisticPatient(nextPatient);
    onClose();

    void onUpdate(
      { patient_id: patient?.patient_id ?? null },
      {
        patient: nextPatient,
      },
    ).catch((error: unknown) => {
      setOptimisticPatient(previousPatient);
      console.error('Failed to update appointment patient:', error);
      enqueueSnackbar('Failed to update patient. Please try again.', { variant: 'error' });
    });
  };

  return (
    <EditablePropertyRow
      icon={PersonOutlined}
      label="Patient"
      disabled={disabled}
      editorMode={editorMode}
      popoverWidth={320}
      value={
        currentPatient ? (
          <PatientValue patient={currentPatient} />
        ) : (
          <EmptyValue>Unassigned</EmptyValue>
        )
      }
      editor={({ onClose }) => (
        <PatientEditor
          patient={currentPatient ?? undefined}
          onChange={patient => commit(patient, onClose)}
        />
      )}
    />
  );
};

const LabelsPropertyRow = ({
  appointment,
  disabled,
  editorMode,
  onUpdate,
}: {
  appointment: Appointment;
  disabled: boolean;
  editorMode: 'floating' | 'inline';
  onUpdate: (
    updates: AppointmentUpdate,
    optimisticAppointmentPatch?: Partial<Appointment>,
  ) => Promise<Appointment>;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const userOrgId = useOrganizationId();
  const labelsEnabled = useAppointmentLabelsEnabled();
  const organizationId = appointment.organization_id ?? userOrgId ?? '';
  const { data: labelsData } = useGetLabelsQuery(
    { orgId: organizationId },
    { skip: !organizationId || !labelsEnabled },
  );
  if (!labelsEnabled) return null;
  const catalog = labelsData?.items ?? [];
  const selectedIds = appointment.labels?.map(label => label.label_id) ?? [];

  const handleChange = (labelIds: string[]) => {
    const resolved = labelIds
      .map(id => catalog.find(label => label.label_id === id))
      .filter((label): label is NonNullable<typeof label> => Boolean(label))
      .map(label => ({
        label_id: label.label_id,
        label_group_id: label.label_group_id,
        name: label.name,
        color: label.color,
      }));
    // Only patch the displayed labels optimistically when every selected id
    // resolved against the loaded catalog — otherwise an in-flight catalog
    // would momentarily blank the icons before the server refetch lands.
    const optimisticPatch = resolved.length === labelIds.length ? { labels: resolved } : undefined;
    void onUpdate({ label_ids: labelIds }, optimisticPatch).catch((error: unknown) => {
      console.error('Failed to update appointment labels:', error);
      enqueueSnackbar('Failed to update labels. Please try again.', { variant: 'error' });
    });
  };

  return (
    <EditablePropertyRow
      icon={LabelOutlined}
      label="Labels"
      disabled={disabled || !organizationId}
      alignTop
      editorMode={editorMode}
      popoverWidth={300}
      value={
        appointment.labels && appointment.labels.length > 0 ? (
          <LabelChipRow labels={appointment.labels} />
        ) : (
          <EmptyValue>No labels</EmptyValue>
        )
      }
      editor={() => (
        <LabelPicker
          organizationId={organizationId}
          value={selectedIds}
          onChange={handleChange}
          autoFocus
          trackingContext={{ scope: 'appointment', surface: 'appointment_popover' }}
        />
      )}
    />
  );
};

const EditablePropertyRow = ({
  icon,
  label,
  value,
  editor,
  disabled = false,
  alignTop = false,
  editorMode = 'floating',
  popoverWidth = 240,
}: {
  icon: ComponentType<SvgIconProps>;
  label: string;
  value: React.ReactNode;
  editor: (props: { onClose: () => void }) => React.ReactNode;
  disabled?: boolean;
  alignTop?: boolean;
  editorMode?: 'floating' | 'inline';
  popoverWidth?: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const onEditorInteraction = useContext(AppointmentPropertyEditorInteractionContext);
  const { refs, floatingStyles, context } = usePropertyEditorFloating(isOpen, setIsOpen);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useDismiss(context),
    useRole(context, { role: 'dialog' }),
  ]);

  if (disabled) {
    return (
      <PropertyRow icon={icon} label={label} alignTop={alignTop}>
        {value}
      </PropertyRow>
    );
  }

  if (editorMode === 'inline') {
    return (
      <Box
        onClick={event => event.stopPropagation()}
        onMouseDown={event => event.stopPropagation()}
        onMouseUp={event => event.stopPropagation()}
      >
        <PropertyRow
          icon={icon}
          label={label}
          alignTop={alignTop}
          active={isOpen}
          onClick={() => {
            onEditorInteraction?.();
            setIsOpen(open => !open);
          }}
        >
          {value}
        </PropertyRow>
        {isOpen && (
          <Box
            sx={{
              mt: 0.75,
              mb: 1,
              borderRadius: 1,
              border: `1px solid ${ELIG_TOKENS.lineStrong}`,
              bgcolor: ELIG_TOKENS.card,
              overflow: 'hidden',
            }}
          >
            {editor({ onClose: () => setIsOpen(false) })}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <>
      <PropertyRow
        icon={icon}
        label={label}
        alignTop={alignTop}
        active={isOpen}
        onClick={() => {
          onEditorInteraction?.();
          setIsOpen(open => !open);
        }}
        referenceRef={refs.setReference}
        interactionProps={getReferenceProps()}
      >
        {value}
      </PropertyRow>
      {isOpen && (
        <FloatingPortal>
          <FloatingEditorSurface
            width={popoverWidth}
            floatingRef={refs.setFloating}
            floatingStyles={floatingStyles}
            floatingProps={getFloatingProps()}
          >
            {editor({ onClose: () => setIsOpen(false) })}
          </FloatingEditorSurface>
        </FloatingPortal>
      )}
    </>
  );
};

const usePropertyEditorFloating = (isOpen: boolean, setIsOpen: (open: boolean) => void) =>
  useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

const FloatingEditorSurface = ({
  width,
  floatingRef,
  floatingStyles,
  floatingProps,
  children,
}: {
  width: number;
  floatingRef: (node: HTMLElement | null) => void;
  floatingStyles: React.CSSProperties;
  floatingProps: React.HTMLAttributes<HTMLElement>;
  children: React.ReactNode;
}) => (
  <Box
    {...floatingProps}
    data-appointment-property-editor-surface="true"
    ref={floatingRef}
    style={{ ...floatingStyles, zIndex: 1500 }}
    sx={{
      // Content-driven width: the configured width is the floor; the surface
      // grows with its widest row (long template names, defaults-preview
      // lines) up to 80vw instead of truncating/wrapping (GDN-2230).
      width: 'max-content',
      minWidth: width,
      maxWidth: '80vw',
      borderRadius: 2,
      border: `1px solid ${ELIG_TOKENS.lineStrong}`,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      bgcolor: ELIG_TOKENS.card,
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
);

const PropertyEditorSearchFrame = (props: PropertyEditorSearchFrameProps) => {
  const onEditorInteraction = useContext(AppointmentPropertyEditorInteractionContext);

  return (
    <SharedPropertyEditorSearchFrame
      {...props}
      onInputFocus={() => {
        props.onInputFocus?.();
        onEditorInteraction?.();
      }}
    />
  );
};

const AppointmentTypeEditor = ({
  value,
  providerId,
  isSaving,
  onChange,
}: {
  value: string | null;
  providerId?: string;
  isSaving: boolean;
  onChange: (
    updates: AppointmentUpdateWithSessionBilling,
    appointmentType: AppointmentType,
  ) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const { appointmentTypes, isLoading } = useAppointmentTypes(searchTerm, providerId);
  // A3 (GDN-2230): template_id -> display name for the per-option preview line.
  const templateNames = useTemplateNameById();

  useEffect(() => {
    setActiveIndex(0);
  }, [appointmentTypes.length, searchTerm]);

  const commitAppointmentType = (appointmentType: AppointmentType) => {
    const isSelected = value === appointmentType.encounter_type_id;
    if (isSelected) return;
    const defaultModality = appointmentType.default_modality ?? null;
    const defaultUseJotmeet = Boolean(
      appointmentType.default_use_jotmeet && defaultModality === 'video',
    );
    const defaultSessionBilling = buildSessionBillingPayload({
      placeOfService: appointmentType.default_place_of_service ?? null,
      cptCodes: appointmentType.default_cpt_codes,
    });

    onChange(
      {
        ...(appointmentType.encounter_type_id
          ? { encounter_type_id: appointmentType.encounter_type_id }
          : { encounter_type: appointmentType.encounter_type }),
        ...(appointmentType.default_duration ? { duration: appointmentType.default_duration } : {}),
        ...(appointmentType.telehealth != null ? { telehealth: appointmentType.telehealth } : {}),
        modality: defaultModality,
        session_billing: defaultSessionBilling,
        use_jotmeet: defaultUseJotmeet,
      },
      appointmentType,
    );
  };

  const commitActiveAppointmentType = () => {
    if (isLoading) return;
    const activeAppointmentType = appointmentTypes[activeIndex];
    if (activeAppointmentType) commitAppointmentType(activeAppointmentType);
  };

  return (
    <PropertyEditorSearchFrame
      search={searchTerm}
      placeholder="Search appointment types..."
      disabled={isSaving}
      activeIndex={activeIndex}
      itemCount={!isLoading ? appointmentTypes.length : 0}
      onSearchChange={setSearchTerm}
      onActiveIndexChange={setActiveIndex}
      onEnter={commitActiveAppointmentType}
    >
      {isLoading ? (
        <EditorLoading />
      ) : appointmentTypes.length === 0 ? (
        <EditorHint>
          {searchTerm ? 'No appointment types match' : 'No appointment types'}
        </EditorHint>
      ) : (
        appointmentTypes.map((appointmentType, index) => {
          const isSelected = value === appointmentType.encounter_type_id;
          // A3 (GDN-2230): what picking this type fills in — template name,
          // duration, visit mode, POS/CPT, forms/reminders — as one quiet line.
          const optionDefaults = mapAppointmentTypeToDefaults(appointmentType, {
            templateName: appointmentType.template_id
              ? templateNames.get(appointmentType.template_id) ?? null
              : null,
          });

          return (
            <OptionRow
              key={appointmentType.encounter_type_id}
              selected={index === activeIndex}
              disabled={isSaving || isSelected}
              onClick={() => commitAppointmentType(appointmentType)}
            >
              <Stack spacing={0.25} sx={{ alignItems: 'flex-start', minWidth: 0, width: '100%' }}>
                <PlainValue>{appointmentType.encounter_type}</PlainValue>
                {countSetDefaults(optionDefaults) > 0 && (
                  <TypeDefaultsPreview
                    variant="inline"
                    defaults={optionDefaults}
                    sx={{ fontSize: 11, color: ELIG_TOKENS.inkSoft }}
                  />
                )}
              </Stack>
            </OptionRow>
          );
        })
      )}
    </PropertyEditorSearchFrame>
  );
};

const PatientEditor = ({
  patient,
  autoFocus = true,
  onChange,
}: {
  patient: Appointment['patient'] | undefined;
  autoFocus?: boolean;
  onChange: (patient: PatientRecord | undefined) => void;
}) => {
  const { getAccessToken } = useAccessToken();
  const canCreatePatients = useCanCreatePatients();
  const subjectLanguage = useSubjectLanguage();
  const { open: openModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();
  const { patient: patientMetrics } = useEventTracking();
  const createPatientEventNameRef = useRef(
    `appointment-property-patient-created-${Math.random().toString(36).slice(2)}`,
  );
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const debouncedSearch = useDebounce(search, 250);
  const [patients, setPatients] = useState<PatientRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const shouldShowCreatePatient = canCreatePatients && !isLoading && search.trim().length > 0;
  const activeItemCount = patients.length + (shouldShowCreatePatient ? 1 : 0);

  useEffect(() => {
    let isActive = true;

    const fetchPatients = async () => {
      setIsLoading(true);
      try {
        const accessToken = await getAccessToken();
        const trimmedSearch = debouncedSearch.trim();
        const requestString = trimmedSearch
          ? `/patient/search?q=${encodeURIComponent(trimmedSearch)}&page=1&per_page=${PATIENT_EDITOR_PAGE_SIZE}`
          : `/patient/list?page=1&per_page=${PATIENT_EDITOR_PAGE_SIZE}`;
        const response = await APIService.makeAPIGetRequest({
          requestString,
          accessToken,
        });

        if (!isActive) return;

        if (response.ok) {
          const data: unknown = await response.value;
          setPatients(getPatientsFromSearchResponse(data));
        } else {
          setPatients([]);
        }
      } catch (error) {
        if (isActive) {
          setPatients([]);
          console.error('Error fetching patients:', error);
        }
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    void fetchPatients();

    return () => {
      isActive = false;
    };
  }, [debouncedSearch, getAccessToken]);

  useEffect(() => {
    setActiveIndex(0);
  }, [patients.length, search]);

  const selectPatient = useCallback(
    (nextPatient: PatientRecord) => {
      patientMetrics.selected(nextPatient.patient_id, {
        source: 'picker',
      });
      onChange(nextPatient);
    },
    [onChange, patientMetrics],
  );

  useEffect(() => {
    const handlePatientCreated = (event: Event) => {
      const patientId = (event as CustomEvent<{ patientId?: string }>).detail?.patientId;
      if (!patientId) return;

      void (async () => {
        try {
          const accessToken = await getAccessToken();
          const response = await APIService.makeAPIGetRequest({
            requestString: `/patient/get?patient_id=${patientId}&origin=AppointmentPropertyPatientCreation`,
            accessToken,
          });

          if (response.ok) {
            const data: unknown = await response.value;
            const createdPatient = getPatientFromGetResponse(data);

            if (createdPatient) {
              setPatients(currentPatients => [
                createdPatient,
                ...currentPatients.filter(
                  currentPatient => currentPatient.patient_id !== createdPatient.patient_id,
                ),
              ]);
              setSearch('');
              selectPatient(createdPatient);
              return;
            }
          }

          enqueueSnackbar(`Created ${subjectLanguage}, but could not select them automatically.`, {
            variant: 'warning',
          });
        } catch (error) {
          console.error('Error selecting newly created patient:', error);
          enqueueSnackbar(`Created ${subjectLanguage}, but could not select them automatically.`, {
            variant: 'warning',
          });
        }
      })();
    };

    const eventName = createPatientEventNameRef.current;
    window.addEventListener(eventName, handlePatientCreated);

    return () => {
      window.removeEventListener(eventName, handlePatientCreated);
    };
  }, [enqueueSnackbar, getAccessToken, selectPatient, subjectLanguage]);

  const openCreatePatientModal = () => {
    if (!canCreatePatients) return;

    openModal(MODAL_TYPES.NEW_PATIENT, {
      source: 'appointment_property_editor',
      navigateToPatients: false,
      simplifiedView: true,
      initialName: search.trim() || undefined,
      onSuccessEventName: createPatientEventNameRef.current,
    });
  };

  const commitActivePatientResult = () => {
    if (isLoading) return;

    const activePatient = patients[activeIndex];
    if (activePatient) {
      selectPatient(activePatient);
      return;
    }

    if (shouldShowCreatePatient && activeIndex === patients.length) {
      openCreatePatientModal();
    }
  };

  return (
    <PropertyEditorSearchFrame
      search={search}
      placeholder={`Search ${subjectLanguage}...`}
      autoFocus={autoFocus}
      disabled={false}
      activeIndex={activeIndex}
      itemCount={!isLoading ? activeItemCount : 0}
      onSearchChange={setSearch}
      onActiveIndexChange={setActiveIndex}
      onEnter={commitActivePatientResult}
    >
      {isLoading && patients.length === 0 && <EditorHint>Loading {subjectLanguage}...</EditorHint>}
      {!isLoading && patients.length === 0 && (
        <EditorHint>
          {search ? `No ${subjectLanguage} match` : `No ${subjectLanguage} available`}
        </EditorHint>
      )}
      {patients.map((nextPatient, index) => (
        <OptionRow
          key={nextPatient.patient_id}
          selected={index === activeIndex}
          onClick={() => selectPatient(nextPatient)}
        >
          <PatientOptionValue patient={nextPatient} />
        </OptionRow>
      ))}
      {shouldShowCreatePatient && (
        <OptionRow
          selected={activeIndex === patients.length}
          disabled={isLoading}
          onClick={openCreatePatientModal}
        >
          <AddIcon sx={{ fontSize: 18, color: ELIG_TOKENS.midnight }} />
          <Stack spacing={0.1} sx={{ minWidth: 0, alignItems: 'flex-start' }}>
            <PlainValue>Create new {subjectLanguage}</PlainValue>
            <Typography variant="caption" sx={{ fontSize: 11, color: ELIG_TOKENS.inkSoft }}>
              {search.trim()}
            </Typography>
          </Stack>
        </OptionRow>
      )}
      <OptionRow selected={!patient && activeItemCount === 0} onClick={() => onChange(undefined)}>
        <EmptyValue>Unassigned</EmptyValue>
      </OptionRow>
    </PropertyEditorSearchFrame>
  );
};

const ProviderEditor = ({
  value,
  clinicians,
  isLoading,
  isSaving,
  onChange,
}: {
  value: string | null;
  clinicians: Array<{ userId: string; name: string }>;
  isLoading: boolean;
  isSaving: boolean;
  onChange: (providerId: string | null) => void;
}) => {
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const normalizedSearch = search.trim().toLowerCase();
  const filteredClinicians = normalizedSearch
    ? clinicians.filter(clinician => clinician.name.toLowerCase().includes(normalizedSearch))
    : clinicians;

  useEffect(() => {
    setActiveIndex(0);
  }, [filteredClinicians.length, search]);

  const commitActiveProvider = () => {
    if (isLoading) return;

    const activeClinician = filteredClinicians[activeIndex];
    if (activeClinician) onChange(activeClinician.userId);
  };

  return (
    <PropertyEditorSearchFrame
      search={search}
      placeholder="Search providers..."
      disabled={isSaving}
      activeIndex={activeIndex}
      itemCount={!isLoading ? filteredClinicians.length : 0}
      onSearchChange={setSearch}
      onActiveIndexChange={setActiveIndex}
      onEnter={commitActiveProvider}
    >
      {isLoading ? (
        <EditorLoading />
      ) : (
        <>
          {filteredClinicians.map((clinician, index) => (
            <OptionRow
              key={clinician.userId}
              selected={index === activeIndex}
              disabled={isSaving}
              onClick={() => onChange(clinician.userId)}
            >
              <ProviderValue name={clinician.name} />
            </OptionRow>
          ))}
          {filteredClinicians.length === 0 && (
            <EditorHint>{search ? 'No providers match' : 'No providers available'}</EditorHint>
          )}
          <OptionRow
            selected={value === null && filteredClinicians.length === 0}
            disabled={isSaving}
            onClick={() => onChange(null)}
          >
            <EmptyValue>Unassigned</EmptyValue>
          </OptionRow>
        </>
      )}
    </PropertyEditorSearchFrame>
  );
};

const STATUS_OPTIONS: DisplayAppointmentStatus[] = [
  'scheduled',
  'confirmed',
  'cancelled',
  'reschedule_requested',
];

const StatusEditor = ({
  value,
  isSaving,
  onChange,
}: {
  value: DisplayAppointmentStatus;
  isSaving: boolean;
  onChange: (value: DisplayAppointmentStatus) => void;
}) => (
  <EditorOptionList>
    {STATUS_OPTIONS.map(status => {
      const option = getStatusDisplayForValue(status);
      return (
        <OptionRow
          key={status}
          selected={status === value}
          disabled={isSaving}
          onClick={() => onChange(status)}
        >
          <StatusValue color={option.color} label={option.label} />
        </OptionRow>
      );
    })}
  </EditorOptionList>
);

const TimeEditor = ({
  value,
  isSaving,
  onClose,
  onChange,
}: {
  value: string;
  isSaving: boolean;
  onClose: () => void;
  onChange: (value: string) => void;
}) => {
  const initialDraft = toDateTimeDraft(value);
  const [draftDate, setDraftDate] = useState(initialDraft.date);
  const [draftTime, setDraftTime] = useState(initialDraft.time);

  useEffect(() => {
    const nextDraft = toDateTimeDraft(value);
    setDraftDate(nextDraft.date);
    setDraftTime(nextDraft.time);
  }, [value]);

  const committedDraft = toDateTimeDraft(value);
  const hasChanged = draftDate !== committedDraft.date || draftTime !== committedDraft.time;
  const canApply =
    isDateDraftValid(draftDate) && isTimeDraftValid(draftTime) && hasChanged && !isSaving;

  const commit = () => {
    if (!canApply) return;

    onChange(new Date(`${draftDate}T${draftTime}`).toISOString());
    onClose();
  };

  return (
    <Stack spacing={1.25} sx={{ p: 1.5 }} onClick={event => event.stopPropagation()}>
      <Stack direction="row" spacing={1}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Date"
          placeholder="YYYY-MM-DD"
          value={draftDate}
          disabled={isSaving}
          onChange={event => setDraftDate(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') commit();
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          fullWidth
          size="small"
          label="Time"
          placeholder="HH:MM"
          value={draftTime}
          disabled={isSaving}
          onChange={event => setDraftTime(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') commit();
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>
      <Button
        size="small"
        variant="contained"
        disabled={!canApply}
        onClick={commit}
        sx={{ alignSelf: 'flex-end', textTransform: 'none' }}
      >
        Apply
      </Button>
    </Stack>
  );
};

const DurationEditor = ({
  value,
  startTime,
  isSaving,
  onChange,
  onClose,
}: {
  value: number;
  startTime: string;
  isSaving: boolean;
  onChange: (value: number) => void;
  onClose: () => void;
}) => {
  const startMs = getValidDateMs(startTime);
  const isCustom = !COMMON_DURATIONS.includes(value as (typeof COMMON_DURATIONS)[number]);
  const [customDraft, setCustomDraft] = useState(isCustom ? String(value) : '');
  const [endDraft, setEndDraft] = useState(() =>
    formatTimeForInput(new Date(startMs + value * 60_000)),
  );

  useEffect(() => {
    setCustomDraft(isCustom ? String(value) : '');
    setEndDraft(formatTimeForInput(new Date(startMs + value * 60_000)));
  }, [isCustom, startMs, value]);

  const commitCustom = () => {
    const duration = parseInt(customDraft, 10);
    if (Number.isFinite(duration) && duration > 0 && duration !== value) {
      onChange(duration);
    }
  };

  const commitEnd = () => {
    const duration = durationFromEndTime(endDraft, startMs);
    if (duration != null && duration > 0 && duration !== value) {
      onChange(duration);
    }
  };

  return (
    <EditorOptionList>
      {COMMON_DURATIONS.map(duration => (
        <OptionRow
          key={duration}
          selected={value === duration}
          disabled={isSaving}
          onClick={() => {
            onChange(duration);
            onClose();
          }}
        >
          <DurationPresetValue
            duration={duration}
            endTime={formatTimeOfDay(new Date(startMs + duration * 60_000))}
          />
        </OptionRow>
      ))}

      <Box sx={{ height: 1, mx: 1, my: 0.5, bgcolor: ELIG_TOKENS.line }} />

      <DurationInputRow
        label="Custom"
        suffix="min"
        type="number"
        placeholder="50"
        value={customDraft}
        selected={isCustom}
        disabled={isSaving}
        inputWidth={56}
        onChange={setCustomDraft}
        onCommit={commitCustom}
      />

      <DurationInputRow
        label="End at"
        type="text"
        placeholder="HH:MM"
        value={endDraft}
        selected={false}
        disabled={isSaving}
        inputWidth={92}
        onChange={setEndDraft}
        onCommit={commitEnd}
      />
    </EditorOptionList>
  );
};

const DurationInputRow = ({
  label,
  value,
  selected,
  disabled,
  type,
  suffix,
  placeholder,
  inputWidth,
  onChange,
  onCommit,
}: {
  label: string;
  value: string;
  selected: boolean;
  disabled: boolean;
  type: 'number' | 'time' | 'text';
  suffix?: string;
  placeholder?: string;
  inputWidth: number;
  onChange: (value: string) => void;
  onCommit: () => void;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto auto',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.75,
      bgcolor: selected ? 'rgba(28, 30, 133, 0.05)' : 'transparent',
    }}
  >
    <Typography
      variant="body2"
      sx={{
        fontSize: 13,
        color: selected ? ELIG_TOKENS.midnight : ELIG_TOKENS.inkSoft,
        fontWeight: selected ? 600 : 400,
        minWidth: 0,
      }}
    >
      {label}
    </Typography>
    <InputBase
      type={type}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={event => onChange(event.target.value)}
      onBlur={onCommit}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      onClick={event => event.stopPropagation()}
      sx={{
        width: inputWidth,
        fontSize: 13,
        color: ELIG_TOKENS.ink,
        border: `1px solid ${ELIG_TOKENS.line}`,
        borderRadius: 1,
        px: 0.75,
        py: 0.25,
        '&.Mui-focused': { borderColor: ELIG_TOKENS.midnight },
        '&.Mui-disabled': {
          color: ELIG_TOKENS.inkFaint,
          bgcolor: 'rgba(0,0,0,0.025)',
        },
        '& input[type=number]::-webkit-inner-spin-button': { display: 'none' },
        '& input[type=number]::-webkit-outer-spin-button': { display: 'none' },
        '& input[type=number]': { MozAppearance: 'textfield' },
      }}
    />
    {suffix && (
      <Typography variant="caption" sx={{ fontSize: 11, color: ELIG_TOKENS.inkFaint }}>
        {suffix}
      </Typography>
    )}
  </Box>
);

const DurationPresetValue = ({ duration, endTime }: { duration: number; endTime: string }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      alignItems: 'baseline',
      gap: 1,
      width: '100%',
      minWidth: 0,
    }}
  >
    <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.ink }}>
      {duration} min
    </Typography>
    <Typography
      variant="caption"
      sx={{ fontSize: 11, color: ELIG_TOKENS.inkFaint, textAlign: 'right', whiteSpace: 'nowrap' }}
    >
      until {endTime}
    </Typography>
  </Box>
);

const formatTimeOfDay = (date: Date): string => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const meridiem = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${hours}:${String(minutes).padStart(2, '0')} ${meridiem}`;
};

const formatTimeForInput = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const durationFromEndTime = (time: string, startMs: number): number | null => {
  const [hoursPart, minutesPart] = time.split(':');
  const hours = parseInt(hoursPart, 10);
  const minutes = parseInt(minutesPart, 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

  const end = new Date(startMs);
  end.setHours(hours, minutes, 0, 0);
  let duration = Math.round((end.getTime() - startMs) / 60_000);
  if (duration <= 0) duration += 24 * 60;
  return duration;
};

const getValidDateMs = (value: string): number => {
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : Date.now();
};

const RecurrenceEditor = ({
  value,
  isSaving,
  includeCustomOption = false,
  onChange,
}: {
  value: RecurrencePresetValue;
  isSaving: boolean;
  includeCustomOption?: boolean;
  onChange: (value: RecurrencePresetValue) => void;
}) => {
  const showCustom = includeCustomOption || value === 'custom';
  // Custom is only non-interactive when an existing custom rule is shown in a
  // context that can't edit it (a create form with no custom handler). Render it
  // faint ONLY then — otherwise the placeholder ink reads as disabled next to the
  // dark preset rows.
  const customDisabled = !includeCustomOption && value === 'custom';
  const customLabel = value === 'custom' ? 'Custom recurrence' : 'Custom recurrence...';

  return (
    <EditorOptionList>
      {RECURRENCE_PRESETS.map(preset => (
        <OptionRow
          key={preset.value}
          selected={value === preset.value}
          disabled={isSaving}
          onClick={() => onChange(preset.value)}
        >
          <PlainValue>{preset.label}</PlainValue>
        </OptionRow>
      ))}
      {showCustom && (
        <>
          <Box sx={{ height: 1, mx: 1, my: 0.5, bgcolor: ELIG_TOKENS.line }} />
          <OptionRow
            selected={value === 'custom'}
            disabled={isSaving || customDisabled}
            onClick={() => onChange('custom')}
          >
            {customDisabled ? (
              <EmptyValue>{customLabel}</EmptyValue>
            ) : (
              <PlainValue>{customLabel}</PlainValue>
            )}
          </OptionRow>
        </>
      )}
    </EditorOptionList>
  );
};

const FormsEditor = ({
  value,
  isSaving,
  onChange,
}: {
  value: AttachedRuleItem[];
  isSaving: boolean;
  onChange: (items: AttachedRuleItem[]) => void;
}) => {
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const debouncedSearch = useDebounce(search, 250);
  const { forms, isLoading } = useForms({
    search: debouncedSearch || undefined,
    pageSize: 50,
  });
  const selectedIds = new Set(value.map(item => item.formDefinitionId).filter(Boolean));
  const availableForms = forms
    .map(formEntryToAttached)
    .filter((item): item is AttachedRuleItem => Boolean(item?.formDefinitionId))
    .filter(item => !selectedIds.has(item.formDefinitionId));

  const commit = (items: AttachedRuleItem[]) => onChange(items);
  const addItem = (item: AttachedRuleItem) =>
    commit([
      ...value,
      {
        ...item,
        offset: DEFAULT_FORM_OFFSET,
        timing: formatRuleTiming(DEFAULT_FORM_OFFSET),
      },
    ]);
  const removeItem = (id: string) => commit(value.filter(item => item.id !== id));
  const updateOffset = (id: string, offset: string) =>
    commit(
      value.map(item =>
        item.id === id ? { ...item, offset, timing: formatRuleTiming(offset) } : item,
      ),
    );

  useEffect(() => {
    setActiveIndex(0);
  }, [availableForms.length, search]);

  const commitActiveForm = () => {
    if (isLoading) return;

    const activeForm = availableForms[activeIndex];
    if (activeForm) addItem(activeForm);
  };

  return (
    <PropertyEditorSearchFrame
      search={search}
      placeholder="Search forms..."
      disabled={isSaving}
      activeIndex={activeIndex}
      itemCount={!isLoading ? availableForms.length : 0}
      selectedContent={
        value.length > 0
          ? value.map(item => (
              <SelectedRuleRow
                key={item.id}
                item={item}
                presets={FORM_TIMING_PRESETS}
                disabled={isSaving}
                onChangeOffset={offset => updateOffset(item.id, offset)}
                onRemove={() => removeItem(item.id)}
              />
            ))
          : undefined
      }
      onSearchChange={setSearch}
      onActiveIndexChange={setActiveIndex}
      onEnter={commitActiveForm}
    >
      {isLoading && forms.length === 0 && <EditorHint>Loading forms...</EditorHint>}
      {!isLoading && availableForms.length === 0 && (
        <EditorHint>{search ? 'No forms match' : 'No forms available'}</EditorHint>
      )}
      {availableForms.map((item, index) => (
        <OptionRow
          key={item.id}
          selected={index === activeIndex}
          disabled={isSaving}
          onClick={() => addItem(item)}
        >
          <RuleCheckbox checked={false} />
          <PlainValue>{item.title}</PlainValue>
        </OptionRow>
      ))}
    </PropertyEditorSearchFrame>
  );
};

const RemindersEditor = ({
  value,
  isSaving,
  onChange,
}: {
  value: AttachedRuleItem[];
  isSaving: boolean;
  onChange: (items: AttachedRuleItem[]) => void;
}) => {
  const selectedIds = new Set(value.map(item => item.reminderTemplateId).filter(Boolean));
  const canAddReminder = !selectedIds.has(APPOINTMENT_REMINDER.reminderTemplateId);

  const commit = (items: AttachedRuleItem[]) => onChange(items);
  const addReminder = () =>
    commit([
      ...value,
      {
        id: APPOINTMENT_REMINDER.id,
        title: APPOINTMENT_REMINDER.title,
        reminderTemplateId: APPOINTMENT_REMINDER.reminderTemplateId,
        offset: DEFAULT_REMINDER_OFFSET,
        timing: formatRuleTiming(DEFAULT_REMINDER_OFFSET),
      },
    ]);
  const removeItem = (id: string) => commit(value.filter(item => item.id !== id));
  const updateOffset = (id: string, offset: string) =>
    commit(
      value.map(item =>
        item.id === id ? { ...item, offset, timing: formatRuleTiming(offset) } : item,
      ),
    );

  return (
    <Box>
      {value.length > 0 && (
        <Box sx={{ borderBottom: `1px solid ${ELIG_TOKENS.line}`, py: 0.5 }}>
          {value.map(item => (
            <SelectedRuleRow
              key={item.id}
              item={item}
              presets={REMINDER_TIMING_PRESETS}
              disabled={isSaving}
              onChangeOffset={offset => updateOffset(item.id, offset)}
              onRemove={() => removeItem(item.id)}
            />
          ))}
        </Box>
      )}
      <EditorOptionList>
        {canAddReminder ? (
          <OptionRow selected={false} disabled={isSaving} onClick={addReminder}>
            <RuleCheckbox checked={false} />
            <PlainValue>{APPOINTMENT_REMINDER.title}</PlainValue>
          </OptionRow>
        ) : (
          <EditorHint>No reminders available</EditorHint>
        )}
      </EditorOptionList>
    </Box>
  );
};

const SelectedRuleRow = ({
  item,
  presets,
  disabled,
  onChangeOffset,
  onRemove,
}: {
  item: AttachedRuleItem;
  presets: readonly TimingPreset[];
  disabled: boolean;
  onChangeOffset: (offset: string) => void;
  onRemove: () => void;
}) => {
  const [timingOpen, setTimingOpen] = useState(false);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          bgcolor: 'rgba(28, 30, 133, 0.04)',
        }}
      >
        <ButtonBase
          disabled={disabled}
          onClick={event => {
            event.stopPropagation();
            onRemove();
          }}
          sx={{ borderRadius: '3px' }}
        >
          <RuleCheckbox checked />
        </ButtonBase>
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 13,
            color: ELIG_TOKENS.ink,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.title}
        </Typography>
        <ButtonBase
          disabled={disabled}
          onClick={event => {
            event.stopPropagation();
            setTimingOpen(open => !open);
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            color: ELIG_TOKENS.inkSoft,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 11 }}>
            {item.timing || 'Set timing'}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: 10 }}>
            v
          </Typography>
        </ButtonBase>
      </Box>
      {timingOpen && (
        <Box sx={{ pl: 4, py: 0.25, bgcolor: 'rgba(28, 30, 133, 0.02)' }}>
          {presets.map(preset => (
            <OptionRow
              key={preset.iso}
              selected={item.offset === preset.iso}
              disabled={disabled}
              onClick={() => {
                onChangeOffset(preset.iso);
                setTimingOpen(false);
              }}
            >
              <Typography variant="body2" sx={{ flex: 1, fontSize: 12, color: ELIG_TOKENS.ink }}>
                {preset.display}
              </Typography>
            </OptionRow>
          ))}
        </Box>
      )}
    </Box>
  );
};

const RuleCheckbox = ({ checked }: { checked: boolean }) => (
  <Box
    sx={{
      width: 14,
      height: 14,
      borderRadius: '3px',
      border: `1.5px solid ${checked ? ELIG_TOKENS.midnight : ELIG_TOKENS.lineStrong}`,
      bgcolor: checked ? ELIG_TOKENS.midnight : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {checked && <Box sx={{ width: 6, height: 6, borderRadius: '1px', bgcolor: '#fff' }} />}
  </Box>
);

const EditorHint = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ px: 1.5, py: 1 }}>
    <Typography variant="caption" sx={{ fontSize: 12, color: ELIG_TOKENS.inkFaint }}>
      {children}
    </Typography>
  </Box>
);

const NotesEditor = ({
  value,
  isSaving,
  onSave,
}: {
  value: string;
  isSaving: boolean;
  onSave: (value: string) => Promise<unknown>;
}) => {
  const [draft, setDraft] = useState(value);
  const debouncedDraft = useDebounce(draft, 650);
  const lastCommittedRef = useRef(value);
  const onEditorInteraction = useContext(AppointmentPropertyEditorInteractionContext);

  useEffect(() => {
    setDraft(value);
    lastCommittedRef.current = value;
  }, [value]);

  useEffect(() => {
    if (debouncedDraft === lastCommittedRef.current) return;

    lastCommittedRef.current = debouncedDraft;
    void onSave(debouncedDraft).catch(() => {
      lastCommittedRef.current = value;
    });
  }, [debouncedDraft, onSave, value]);

  return (
    <Stack spacing={0.75} sx={{ p: 1.25 }}>
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={4}
        maxRows={8}
        size="small"
        placeholder="Add notes"
        value={draft}
        onFocus={onEditorInteraction ?? undefined}
        onChange={event => {
          onEditorInteraction?.();
          setDraft(event.target.value);
        }}
      />
      <Typography
        variant="caption"
        sx={{
          minHeight: 16,
          fontSize: 11,
          color: isSaving ? ELIG_TOKENS.inkFaint : 'transparent',
        }}
      >
        Saving...
      </Typography>
    </Stack>
  );
};

const EditorLoading = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1 }}>
    <CircularProgress size={14} />
    <EmptyValue>Loading...</EmptyValue>
  </Box>
);

const PropertyRow = ({
  icon: Icon,
  label,
  children,
  alignTop = false,
  active = false,
  onClick,
  referenceRef,
  interactionProps,
}: {
  icon: ComponentType<SvgIconProps>;
  label: string;
  children: React.ReactNode;
  alignTop?: boolean;
  active?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  referenceRef?: (node: HTMLElement | null) => void;
  interactionProps?: React.HTMLAttributes<HTMLElement>;
}) => {
  const content = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: ELIG_TOKENS.inkSoft }}>
        <Icon sx={{ fontSize: 14, mt: alignTop ? 0.25 : 0 }} />
        <Typography
          variant="body2"
          sx={{ fontSize: 12, fontWeight: 500, color: ELIG_TOKENS.inkSoft }}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </>
  );

  const sx = {
    display: 'grid',
    gridTemplateColumns: '120px minmax(0, 1fr)',
    alignItems: alignTop ? 'flex-start' : 'center',
    gap: 1,
    py: 0.75,
    px: 0.5,
    borderRadius: 1,
    transition: 'background-color 0.12s',
    ...(onClick
      ? {
          cursor: 'pointer',
          ...(active
            ? { bgcolor: 'rgba(0,0,0,0.04)' }
            : { '&:hover': { bgcolor: 'rgba(0,0,0,0.025)' } }),
        }
      : {}),
  };

  if (!onClick) return <Box sx={sx}>{content}</Box>;

  return (
    <Box
      {...interactionProps}
      ref={referenceRef}
      component="button"
      type="button"
      onClick={onClick}
      sx={{ ...sx, border: 0, bgcolor: 'transparent', width: '100%', textAlign: 'left' }}
    >
      {content}
    </Box>
  );
};

const OptionRow = ({
  selected,
  disabled = false,
  onClick,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => {
  const committedOnPointerDownRef = useRef(false);
  const onEditorInteraction = useContext(AppointmentPropertyEditorInteractionContext);

  const commit = () => {
    if (disabled) return;
    onEditorInteraction?.();
    onClick();
  };

  const commitFromPress = (event: React.MouseEvent | React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (committedOnPointerDownRef.current) return;
    committedOnPointerDownRef.current = true;
    commit();
  };

  return (
    <ButtonBase
      disabled={disabled}
      onPointerDown={commitFromPress}
      onMouseDown={commitFromPress}
      onClick={event => {
        event.stopPropagation();
        if (committedOnPointerDownRef.current) {
          committedOnPointerDownRef.current = false;
          return;
        }
        commit();
      }}
      sx={{
        // Block-level flex (ButtonBase defaults to inline-flex): under the
        // editor surface's width:max-content, inline-level rows would lay out
        // side-by-side during intrinsic sizing and the popover would size to
        // the SUM of all option widths instead of the widest row (GDN-2230).
        display: 'flex',
        width: '100%',
        justifyContent: 'flex-start',
        gap: 1,
        px: 1.5,
        py: 0.75,
        color: ELIG_TOKENS.ink,
        textAlign: 'left',
        bgcolor: selected ? 'rgba(28, 30, 133, 0.06)' : 'transparent',
        '&:hover': { bgcolor: selected ? 'rgba(28, 30, 133, 0.08)' : 'rgba(0,0,0,0.035)' },
        '&.Mui-disabled': {
          color: ELIG_TOKENS.inkFaint,
        },
      }}
    >
      {children}
    </ButtonBase>
  );
};

const DotValue = ({ color, label }: { color: string; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: color,
        flexShrink: 0,
      }}
    />
    <PlainValue>{label}</PlainValue>
  </Box>
);

const StatusValue = ({ color, label }: { color: string; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
    <PlainValue>{label}</PlainValue>
  </Box>
);

const PatientOptionValue = ({ patient }: { patient: PatientRecord }) => {
  const details = [
    patient.identifier,
    patient.dob ? formatPatientDob(patient.dob) : null,
    formatPatientGenderOrSex(patient.gender ?? patient.sex),
  ].filter((detail): detail is string => Boolean(detail));

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
      <Avatar
        id={patient.patient_id}
        firstName={patient.first_name}
        lastName={patient.last_name}
        sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, flexShrink: 0 }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <PlainValue>{formatPatientRecordName(patient)}</PlainValue>
        {details.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.125,
              color: ELIG_TOKENS.inkFaint,
              fontSize: 11,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {details.join(' · ')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ProviderValue = ({ name }: { name: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <ProviderBadge name={name} />
    <PlainValue>{name}</PlainValue>
  </Box>
);

const PatientValue = ({
  patient,
}: {
  patient: PatientRecord | NonNullable<Appointment['patient']>;
}) => {
  const patientName = formatPatientValueName(patient);
  const firstName = 'first_name' in patient ? (patient.first_name ?? undefined) : undefined;
  const lastName = 'last_name' in patient ? (patient.last_name ?? undefined) : undefined;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Avatar
        id={patient.patient_id}
        firstName={firstName}
        lastName={lastName}
        sx={{ width: 20, height: 20, fontSize: 9, fontWeight: 700, flexShrink: 0 }}
      />
      <PlainValue>{patientName}</PlainValue>
    </Box>
  );
};

const PlainValue = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="body2"
    sx={{
      fontSize: 13,
      color: ELIG_TOKENS.ink,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
);

const EmptyValue = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.inkFaint }}>
    {children}
  </Typography>
);

const ChipList = ({ items, emptyLabel }: { items: AttachedRuleItem[]; emptyLabel: string }) => {
  if (items.length === 0) return <EmptyValue>{emptyLabel}</EmptyValue>;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {items.map(item => (
        <Box
          key={item.id}
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 0.5,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: 'rgba(28, 30, 133, 0.05)',
            border: `1px solid ${ELIG_TOKENS.line}`,
            lineHeight: 1.4,
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 11, color: ELIG_TOKENS.ink }}>
            {item.title}
          </Typography>
          {item.timing ? (
            <Typography variant="caption" sx={{ fontSize: 10, color: ELIG_TOKENS.inkFaint }}>
              {item.timing}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );
};

const getAppointmentProviderName = (appointment: Appointment): string | null => {
  const provider = appointment.provider as { name?: unknown } | null | undefined;
  return typeof provider?.name === 'string' && provider.name ? provider.name : null;
};

const lookupProviderName = (
  providerId: string | null | undefined,
  clinicians: Array<{ userId: string; name: string }>,
): string | null => {
  if (!providerId) return null;
  return clinicians.find(clinician => clinician.userId === providerId)?.name ?? 'Assigned provider';
};

const mapFormRules = (rules: Appointment['form_rules']): AttachedRuleItem[] =>
  rules
    ?.map((rule, index): AttachedRuleItem | null => {
      const formDefinitionId = getRuleString(rule, 'form_definition_id');
      const ruleId = getRuleString(rule, 'id');
      const offset = getRuleString(rule, 'offset') ?? '';
      const title = getRuleString(rule, 'title') ?? formDefinitionId ?? `Form ${index + 1}`;
      if (!formDefinitionId && !title) return null;
      return {
        id: ruleId ?? formDefinitionId ?? `form-${index}`,
        title,
        offset,
        timing: formatRuleTiming(offset),
        formDefinitionId: formDefinitionId ?? undefined,
        ruleId,
      };
    })
    .filter((item): item is AttachedRuleItem => item !== null) ?? [];

const mapReminderRules = (rules: Appointment['reminder_rules']): AttachedRuleItem[] =>
  rules
    ?.map((rule, index): AttachedRuleItem | null => {
      const reminderTemplateId = getRuleString(rule, 'reminder_template_id');
      const ruleId = getRuleString(rule, 'id');
      const offset = getRuleString(rule, 'offset') ?? '';
      const title = getRuleString(rule, 'title') ?? reminderTemplateId ?? `Reminder ${index + 1}`;
      if (!reminderTemplateId && !title) return null;
      return {
        id: ruleId ?? reminderTemplateId ?? `reminder-${index}`,
        title,
        offset,
        timing: formatRuleTiming(offset),
        reminderTemplateId: reminderTemplateId ?? undefined,
        ruleId,
      };
    })
    .filter((item): item is AttachedRuleItem => item !== null) ?? [];

const toFormRules = (items: AttachedRuleItem[]): AppointmentFormRule[] =>
  items
    .filter(item => item.formDefinitionId)
    .map(item => ({
      form_definition_id: item.formDefinitionId ?? '',
      offset: item.offset,
      title: item.title,
      ...(item.ruleId ? { id: item.ruleId } : {}),
    }));

const toReminderRules = (items: AttachedRuleItem[]): AppointmentReminderRule[] =>
  items
    .filter(item => item.reminderTemplateId)
    .map(item => ({
      reminder_template_id: item.reminderTemplateId ?? '',
      offset: item.offset,
      title: item.title,
      ...(item.ruleId ? { id: item.ruleId } : {}),
    }));

const formEntryToAttached = (entry: FormWithRevisionSummary): AttachedRuleItem | null => {
  const formDefinitionId = entry.form.id;
  const title = entry.current_revision?.title ?? null;
  if (!formDefinitionId || !title) return null;
  return {
    id: formDefinitionId,
    title,
    formDefinitionId,
    offset: DEFAULT_FORM_OFFSET,
    timing: formatRuleTiming(DEFAULT_FORM_OFFSET),
  };
};

const getRuleString = (rule: unknown, key: string): string | null => {
  if (!rule || typeof rule !== 'object' || !(key in rule)) return null;
  const value = (rule as Record<string, unknown>)[key];
  return typeof value === 'string' && value ? value : null;
};

const formatRuleTiming = (offset: string): string => {
  if (!offset) return '';
  try {
    return formatAppointmentDuration(offset);
  } catch {
    return offset;
  }
};

const getRecurrencePresetValue = (rrule: string | null | undefined): RecurrencePresetValue => {
  if (!rrule) return 'none';
  try {
    const rule = rrulestr(rrule);
    if (!(rule instanceof RRule)) return 'custom';

    if (rule.options.freq === RRule.DAILY) return 'daily';
    if (rule.options.freq === RRule.WEEKLY) {
      return rule.options.interval === 2 ? 'biweekly' : 'weekly';
    }
    if (rule.options.freq === RRule.MONTHLY) return 'monthly';
    if (rule.options.freq === RRule.YEARLY) return 'yearly';
    return 'custom';
  } catch {
    return 'custom';
  }
};

const getRRuleForRecurrencePreset = (value: RecurrencePresetValue): string | null => {
  switch (value) {
    case 'daily':
      return new RRule({ freq: RRule.DAILY }).toString();
    case 'weekly':
      return new RRule({ freq: RRule.WEEKLY }).toString();
    case 'biweekly':
      return new RRule({ freq: RRule.WEEKLY, interval: 2 }).toString();
    case 'monthly':
      return new RRule({ freq: RRule.MONTHLY }).toString();
    case 'yearly':
      return new RRule({ freq: RRule.YEARLY }).toString();
    case 'none':
    case 'custom':
      return null;
  }
};

const coerceVisitStatus = (status: string | null | undefined): VisitStatus =>
  status && VISIT_STATUSES.includes(status as VisitStatus)
    ? (status as VisitStatus)
    : 'not_started';

const toDateTimeLocalValue = (value: string): string => {
  try {
    return format(parseISO(value), "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
};

const toDateTimeDraft = (value: string): { date: string; time: string } => {
  const localValue = toDateTimeLocalValue(value);
  const [date = '', time = ''] = localValue.split('T');
  return { date, time };
};

const isDateDraftValid = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

const isTimeDraftValid = (value: string): boolean => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

const mapPatientRecordToAppointmentPatient = (
  patient: PatientRecord | undefined,
): Appointment['patient'] | null => {
  if (!patient) return null;

  const name = [patient.first_name, patient.middle_name, patient.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  return {
    dob: patient.dob ?? null,
    first_name: patient.first_name ?? null,
    identifier: patient.identifier ?? null,
    last_name: patient.last_name ?? null,
    middle_name: patient.middle_name ?? null,
    name: name || null,
    patient_id: patient.patient_id,
    preferred_name: patient.preferred_name ?? null,
    telephone: getPatientPrimaryContactPhone(patient) || null,
  };
};

const toCreateStartIso = (date: string, time: string): string => {
  if (isDateDraftValid(date) && isTimeDraftValid(time)) {
    return new Date(`${date}T${time}`).toISOString();
  }
  return new Date().toISOString();
};

const formatPatientRecordName = (patient: PatientRecord): string => {
  const name = [patient.first_name, patient.middle_name, patient.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  return patient.preferred_name || name || patient.identifier || 'Unnamed patient';
};

const formatPatientValueName = (
  patient: PatientRecord | NonNullable<Appointment['patient']>,
): string => {
  if ('name' in patient && patient.name) return patient.name;

  const name = [
    'first_name' in patient ? patient.first_name : null,
    'middle_name' in patient ? patient.middle_name : null,
    'last_name' in patient ? patient.last_name : null,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    ('preferred_name' in patient ? patient.preferred_name : null) ||
    name ||
    patient.identifier ||
    'Unnamed patient'
  );
};

const formatPatientDob = (dob: string): string => {
  try {
    return format(parseISO(dob), 'MM/dd/yy');
  } catch {
    return dob;
  }
};

const formatPatientGenderOrSex = (genderOrSex: string | null | undefined): string | null => {
  if (!genderOrSex) return null;

  const normalizedValue = genderOrSex.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

  if (!normalizedValue || normalizedValue.toLowerCase() === 'unknown') return null;

  return normalizedValue.replace(/\b\w/g, character => character.toUpperCase());
};

const getPatientsFromSearchResponse = (data: unknown): PatientRecord[] => {
  if (!data || typeof data !== 'object' || !('patients' in data)) return [];

  const patients = (data as { patients?: unknown }).patients;
  return Array.isArray(patients) ? patients.filter(isPatientRecord) : [];
};

const getPatientFromGetResponse = (data: unknown): PatientRecord | null => {
  if (!data || typeof data !== 'object' || !('patient' in data)) return null;

  const patient = (data as { patient?: unknown }).patient;
  return isPatientRecord(patient) ? patient : null;
};

const isPatientRecord = (value: unknown): value is PatientRecord =>
  Boolean(
    value &&
    typeof value === 'object' &&
    'patient_id' in value &&
    typeof (value as { patient_id?: unknown }).patient_id === 'string',
  );

const coerceRecurrencePresetValue = (value: string): RecurrencePresetValue => {
  if (RECURRENCE_PRESETS.some(preset => preset.value === value)) {
    return value as RecurrencePresetValue;
  }
  if (value === 'custom') return 'custom';
  return 'none';
};

const getCreateRecurrenceLabel = (value: string): string => {
  const preset = RECURRENCE_PRESETS.find(candidate => candidate.value === value);
  if (preset) return preset.label;
  if (value === 'custom') return 'Custom';
  return 'One-time';
};

const expandableItemToAttachedRuleItem = (item: ExpandableFieldItem): AttachedRuleItem => ({
  id: String(item.id),
  title: item.name,
  offset: item.offset ?? '',
  timing: item.timing ?? formatRuleTiming(item.offset ?? ''),
  formDefinitionId: item.formId,
  // ExpandableFieldItem does not expose a reminder template id; existing create submission
  // maps reminders from item.name, so preserve that compatibility at this adapter boundary.
  reminderTemplateId: item.formId ? undefined : item.name,
  ruleId: item.ruleId ?? null,
});

const attachedRuleItemToExpandableItem = (item: AttachedRuleItem): ExpandableFieldItem => ({
  id: item.id,
  name: item.title,
  timing: item.timing,
  offset: item.offset,
  formId: item.formDefinitionId,
  status: 'configured',
  ruleId: item.ruleId ?? undefined,
});

const getStatusDisplay = (appointment: Appointment): { label: string; color: string } => {
  return getStatusDisplayForValue(getDisplayAppointmentStatus(appointment), appointment);
};

const getDisplayAppointmentStatus = (appointment: Appointment): DisplayAppointmentStatus => {
  const reality = appointment.block_state?.reality;
  if (appointment.status === 'cancelled' || reality === 'cancelled') return 'cancelled';
  if (appointment.confirmation_status === 'cancelled') {
    return 'cancelled';
  }
  if (appointment.confirmation_status === 'reschedule_requested' || reality === 'rescheduling') {
    return 'reschedule_requested';
  }
  if (appointment.confirmation_status === 'confirmed') return 'confirmed';
  return 'scheduled';
};

const getStatusDisplayForValue = (
  status: DisplayAppointmentStatus,
  appointment?: Appointment,
): { label: string; color: string } => {
  if (appointment?.block_state?.reality === 'suggested' || appointment?.status === 'suggested') {
    return { label: 'Suggested', color: ELIG_TOKENS.midnight };
  }
  if (appointment?.block_state?.reality === 'tentative') {
    return { label: 'Tentative', color: ELIG_TOKENS.gray };
  }
  if (appointment?.block_state?.reality === 'block') {
    return { label: 'Calendar block', color: ELIG_TOKENS.midnight };
  }

  switch (status) {
    case 'cancelled':
      return { label: 'Cancelled', color: ELIG_TOKENS.red };
    case 'confirmed':
      return { label: 'Confirmed', color: ELIG_TOKENS.green };
    case 'reschedule_requested':
      return { label: 'Reschedule requested', color: ELIG_TOKENS.amber };
    case 'scheduled':
      return { label: 'Scheduled', color: ELIG_TOKENS.gray };
  }
};

const getStatusTransitionForDisplayStatus = (
  appointment: Appointment,
  status: DisplayAppointmentStatus,
): { axis: 'slot' | 'response'; to: string } => {
  switch (status) {
    case 'cancelled':
      return { axis: 'slot', to: 'cancelled' };
    case 'confirmed':
      return { axis: 'response', to: 'confirmed' };
    case 'reschedule_requested':
      return { axis: 'response', to: 'reschedule_requested' };
    case 'scheduled':
      if (appointment.status === 'cancelled') {
        return { axis: 'slot', to: 'scheduled' };
      }
      return { axis: 'response', to: 'unconfirmed' };
  }
};

const getOptimisticPatchForStatusTransition = (
  appointment: Appointment,
  transition: { axis: 'slot' | 'response'; to: string },
): Partial<Appointment> => {
  const now = new Date().toISOString();
  const blockStatePatch = getBlockStatePatchForStatusTransition(transition);

  if (transition.axis === 'slot') {
    return {
      status: transition.to,
      status_changed_at: now,
      status_change_source: 'calendar_status_property',
      ...(transition.to === 'cancelled' ? { cancelled_at: now } : {}),
      ...(transition.to === 'scheduled'
        ? {
            cancelled_at: null,
            cancellation_source: null,
            cancellation_reason: null,
            cancelled_by_actor_type: null,
            cancelled_by_user_id: null,
          }
        : {}),
      ...(blockStatePatch && appointment.block_state ? { block_state: blockStatePatch } : {}),
    } as Partial<Appointment>;
  }

  return {
    confirmation_status: transition.to,
    confirmation_status_changed_at: now,
    confirmation_status_change_source: 'calendar_status_property',
    ...(blockStatePatch && appointment.block_state ? { block_state: blockStatePatch } : {}),
  } as Partial<Appointment>;
};

const getBlockStatePatchForStatusTransition = (transition: {
  axis: 'slot' | 'response';
  to: string;
}): Partial<NonNullable<Appointment['block_state']>> | undefined => {
  if (transition.axis === 'slot') {
    if (transition.to === 'cancelled') return { reality: 'cancelled' };
    if (transition.to === 'scheduled') return { reality: 'real' };
  }

  if (transition.to === 'cancelled') return { reality: 'cancelled' };
  if (transition.to === 'reschedule_requested') return { reality: 'rescheduling' };
  if (transition.to === 'confirmed') return { reality: 'real', qualifiers: { confirmed: true } };
  if (transition.to === 'unconfirmed') return { reality: 'real', qualifiers: { confirmed: false } };

  return undefined;
};
